<template>
  <div class="spawn-editor animated fadeIn">
    <div class="spawn-layout">
      <aside class="spawn-map-panel">
        <div class="spawn-panel-header">
          <div>
            <div class="spawn-panel-title">Maps</div>
            <small class="text-muted">{{ sidebarSummary }}</small>
          </div>
          <b-button size="sm" variant="outline-info" :disabled="loading" @click="refreshSpawns">
            <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
          </b-button>
        </div>

        <div class="spawn-workflow-tabs">
          <b-button-group size="sm">
            <b-button
              v-for="view in visibleWorkflowViewOptions"
              :key="'spawn-workflow-view-' + view.value"
              :variant="workflowView === view.value ? 'info' : 'outline-info'"
              @click="setWorkflowView(view.value)"
            >
              {{ view.text }}
            </b-button>
          </b-button-group>
          <b-button
            v-if="workflowView === 'wip' && !editMode"
            size="sm"
            variant="outline-success"
            @click="openNewMapModal"
          >
            <i class="fa fa-plus mr-1"></i>New Map
          </b-button>
        </div>

        <b-input-group size="sm" class="spawn-map-search">
          <b-input-group-prepend is-text class="bg-dark border-secondary">
            <i class="fa fa-search small"></i>
          </b-input-group-prepend>
          <b-form-input
            v-model.trim="mapSearch"
            :placeholder="mapSearchPlaceholder"
            class="bg-dark text-white border-secondary"
          ></b-form-input>
          <b-input-group-append>
            <b-form-select
              v-model="mapSearchMode"
              :options="mapSearchModeOptions"
              size="sm"
              class="spawn-map-search-mode bg-dark text-white border-secondary"
            ></b-form-select>
          </b-input-group-append>
        </b-input-group>

        <div class="spawn-map-list">
          <div v-if="selectedSearchEntity" class="spawn-search-context">
            <div class="text-muted small">{{ selectedSearchEntityLabel }}</div>
            <b-button size="sm" variant="outline-secondary" @click="clearSearchEntity">
              <i class="fa fa-times"></i>
            </b-button>
          </div>

          <button
            v-for="item in sidebarItems"
            :key="sidebarItemKey(item)"
            type="button"
            class="spawn-map-item"
            :class="{ active: item.map && item.map.name === activeMap, 'spawn-map-item-stacked': item.type === 'pokemonMap' }"
            @click="selectSidebarItem(item)"
          >
            <template v-if="item.type === 'pokemonOption'">
              <span class="spawn-sidebar-entity">
                <span class="spawn-sidebar-sprite-wrap">
                  <img
                    v-if="!spriteErrors['search-' + item.dexID]"
                    class="spawn-sidebar-sprite"
                    :src="pokemonSpriteUrl(item.dexID)"
                    :alt="item.name"
                    :data-fallback-id="pokemonFallbackId(item.dexID)"
                    @error="handleSearchSpriteError(item, $event)"
                  >
                  <i v-else class="fa fa-question"></i>
                </span>
                <span class="spawn-map-name" :class="{ 'spawn-membership-name': item.memberOnly }" v-html="highlightMapSearch(item.name)"></span>
                <span
                  v-if="showWarnings && item.repelInfo"
                  class="spawn-repel-inline-icon"
                  v-b-tooltip.html.hover
                  :title="repelInfoTooltip(item.repelInfo)"
                >
                  <img v-if="!iconErrors.repel" :src="repelIconUrl" alt="Repel" @error="markIconError('repel')">
                  <i v-else class="fa fa-shield"></i>
                </span>
              </span>
              <span class="badge badge-secondary">{{ item.mapCount }}</span>
            </template>

            <template v-else-if="item.type === 'regionOption'">
              <span class="spawn-map-name" v-html="highlightMapSearch(item.name)"></span>
              <span class="badge badge-secondary">{{ item.mapCount }}</span>
            </template>

            <template v-else-if="item.type === 'pokemonMap'">
              <span class="spawn-map-result">
                <span class="spawn-map-header-line">
                  <span class="spawn-map-name" v-html="highlightMapSearch(item.map.name)"></span>
                  <span class="spawn-map-counts">
                    <span class="badge badge-success">{{ item.map.land }}</span>
                    <span class="badge badge-info">{{ item.map.water }}</span>
                  </span>
                </span>
                <span class="spawn-pokemon-map-line">
                  <span class="spawn-sidebar-sprite-wrap">
                    <img
                      v-if="!spriteErrors['search-map-' + item.dexID + '-' + item.map.name]"
                      class="spawn-sidebar-sprite"
                      :src="pokemonSpriteUrl(item.dexID)"
                      :alt="item.pokemonName"
                      :data-fallback-id="pokemonFallbackId(item.dexID)"
                      @error="handleSearchMapSpriteError(item, $event)"
                    >
                    <i v-else class="fa fa-question"></i>
                  </span>
                  <span :class="{ 'spawn-membership-name': item.memberOnly }">{{ item.pokemonName }}</span>
                  <span class="spawn-tier spawn-tier-compact" :class="'tier-group-' + item.tierGroup">{{ item.tierLabel }}</span>
                  <span
                    v-if="showWarnings && item.repelInfo"
                    class="spawn-repel-inline-icon"
                    v-b-tooltip.html.hover
                    :title="repelInfoTooltip(item.repelInfo)"
                  >
                    <img v-if="!iconErrors.repel" :src="repelIconUrl" alt="Repel" @error="markIconError('repel')">
                    <i v-else class="fa fa-shield"></i>
                  </span>
                </span>
              </span>
            </template>

            <template v-else-if="item.type === 'regionMap'">
              <span class="spawn-map-result">
                <span class="spawn-map-header-line">
                  <span class="spawn-map-name" v-html="highlightMapSearch(item.map.name)"></span>
                  <span class="spawn-map-counts">
                    <span class="badge badge-success">{{ item.map.land }}</span>
                    <span class="badge badge-info">{{ item.map.water }}</span>
                  </span>
                </span>
                <span class="spawn-region-map-line" v-html="highlightMapSearch(item.region)"></span>
              </span>
            </template>

            <template v-else>
              <span class="spawn-map-name" v-html="highlightMapSearch(item.map.name)"></span>
              <span class="spawn-map-counts">
                <span class="badge badge-success">{{ item.map.land }}</span>
                <span class="badge badge-info">{{ item.map.water }}</span>
              </span>
            </template>
          </button>

          <div v-if="sidebarItems.length === 0" class="text-muted text-center py-4">
            No entries found.
          </div>
        </div>
      </aside>

      <main class="spawn-content">
        <b-card no-body class="spawn-toolbar-card">
          <div class="spawn-toolbar">
            <div class="spawn-title-block">
              <div class="spawn-title-row">
                <i class="fa fa-map text-info"></i>
                <strong>Spawn Editor</strong>
              </div>
              <div class="text-muted small">
                {{ activeMap || 'No map selected' }}
                <span v-if="selectedMapInfo"> - {{ selectedMapInfo.land }} land, {{ selectedMapInfo.water }} water</span>
              </div>
            </div>

            <div class="spawn-controls">
              <div class="spawn-control-group">
                <b-button-group size="sm">
                  <b-button :variant="displayMode === 'stacked' ? 'info' : 'outline-info'" @click="displayMode = 'stacked'">
                    <i class="fa fa-list mr-1"></i> Stacked
                  </b-button>
                  <b-button :variant="displayMode === 'land' ? 'success' : 'outline-success'" @click="displayMode = 'land'">
                    <i class="fa fa-tree mr-1"></i> Land
                  </b-button>
                  <b-button :variant="displayMode === 'water' ? 'primary' : 'outline-primary'" @click="displayMode = 'water'">
                    <i class="fa fa-tint mr-1"></i> Water
                  </b-button>
                </b-button-group>
              </div>

              <div class="spawn-control-group">
                <span class="spawn-control-label">Sort</span>
                <b-form-select
                  v-model="sortKey"
                  :options="sortOptions"
                  size="sm"
                  class="spawn-sort-select bg-dark text-white border-secondary"
                ></b-form-select>
                <b-button
                  size="sm"
                  variant="outline-info"
                  class="spawn-sort-direction"
                  @click="toggleSortDirection"
                >
                  <i class="fa mr-1" :class="sortDirection === 'asc' ? 'fa-sort-amount-asc' : 'fa-sort-amount-desc'"></i>
                  {{ sortDirection === 'asc' ? 'Asc' : 'Desc' }}
                </b-button>
              </div>

              <div class="spawn-control-group">
                <span class="spawn-control-label">Time</span>
                <b-button-group size="sm">
                  <b-button
                    v-for="filter in timeFilters"
                    :key="'time-filter-' + filter.key"
                    :variant="activeTimeFilter === filter.key ? 'info' : 'outline-info'"
                    @click="activeTimeFilter = filter.key"
                  >
                    {{ filter.label }}
                  </b-button>
                </b-button-group>
              </div>

              <b-button
                size="sm"
                class="spawn-membership-toggle"
                :variant="membershipActive ? 'info' : 'outline-secondary'"
                @click="membershipActive = !membershipActive"
              >
                <i class="fa mr-1" :class="membershipActive ? 'fa-toggle-on' : 'fa-toggle-off'"></i>
                Membership: {{ membershipActive ? 'Active' : 'Inactive' }}
              </b-button>

              <b-button
                size="sm"
                class="spawn-warning-toggle"
                :variant="showWarnings ? 'warning' : 'outline-secondary'"
                @click="showWarnings = !showWarnings"
              >
                <i class="fa mr-1" :class="showWarnings ? 'fa-eye' : 'fa-eye-slash'"></i>
                Warnings: {{ showWarnings ? 'On' : 'Off' }}
              </b-button>
            </div>
          </div>
        </b-card>

        <div v-if="loading && spawns.length === 0" class="spawn-loading">
          <b-spinner variant="info" label="Loading"></b-spinner>
          <div class="text-muted mt-2">Loading spawns...</div>
        </div>

        <div v-else-if="!activeMap" class="spawn-empty">
          <i class="fa fa-map-o"></i>
          <div>No spawn maps available.</div>
        </div>

        <template v-else>
          <div v-if="activeBatch && workflowView !== 'live' && !editMode" class="spawn-workflow-panel">
            <div class="spawn-workflow-meta">
              <div>
                <strong>{{ activeBatch.mapName }}</strong>
                <small class="text-muted d-block">Created by {{ activeBatch.creatorName }} - {{ workflowStatusLabel(activeBatch.status) }}</small>
              </div>
              <div class="spawn-workflow-actions">
                <b-button
                  v-if="activeBatch.isOwner && activeBatch.status === 'wip'"
                  size="sm"
                  variant="outline-info"
                  @click="moveActiveBatch('review')"
                >
                  Move to Review Spawns
                </b-button>
                <b-button
                  v-if="activeBatch.isOwner && activeBatch.status === 'review'"
                  size="sm"
                  variant="outline-success"
                  @click="moveActiveBatch('ready')"
                >
                  Move to Ready for Upload
                </b-button>
                <b-button
                  v-if="canReviewActiveBatch"
                  size="sm"
                  variant="outline-success"
                  @click="reviewActiveBatch('approved')"
                >
                  Approve
                </b-button>
                <b-button
                  v-if="canReviewActiveBatch"
                  size="sm"
                  variant="outline-danger"
                  @click="reviewActiveBatch('rejected')"
                >
                  Reject
                </b-button>
                <b-button
                  v-if="activeBatch.isOwner"
                  size="sm"
                  variant="outline-warning"
                  @click="removeActiveBatch"
                >
                  {{ activeBatch.status === 'wip' ? 'Remove from Workspace' : 'Move back to Workspace' }}
                </b-button>
                <b-button
                  v-if="workflowCanUploadReady && activeBatch.status === 'ready'"
                  size="sm"
                  variant="success"
                  @click="uploadActiveBatch"
                >
                  Upload Ready Spawns
                </b-button>
                <b-button
                  v-if="workflowCanUploadReady && activeBatch.status === 'ready'"
                  size="sm"
                  variant="outline-danger"
                  @click="rejectReadyActiveBatch"
                >
                  Reject Ready Spawns
                </b-button>
              </div>
            </div>

            <div v-if="activeBatch.reviews && activeBatch.reviews.length" class="spawn-review-list">
              <span
                v-for="review in activeBatch.reviews"
                :key="'review-' + review.id"
                class="spawn-review-pill"
                :class="'spawn-review-' + review.decision"
              >
                {{ reviewDecisionLabel(review) }}
              </span>
            </div>

            <div class="spawn-compare-table">
              <div class="spawn-compare-row spawn-compare-head">
                <span>Change</span>
                <span>Area</span>
                <span>Pokemon</span>
                <span>Live</span>
                <span>New</span>
              </div>
              <div
                v-for="row in comparisonRowsForActiveBatch()"
                :key="row.key"
                class="spawn-compare-row"
              >
                <span>{{ row.type }}</span>
                <span>{{ row.area }}</span>
                <span>{{ row.pokemon }}</span>
                <span>{{ row.before }}</span>
                <span>{{ row.after }}</span>
              </div>
              <div v-if="comparisonRowsForActiveBatch().length === 0" class="spawn-compare-empty">
                No effective differences.
              </div>
            </div>
          </div>

          <div v-if="editMode && editRows.length === 0" class="spawn-empty-level-range">
            <span>Default Level Range</span>
            <b-form-input :value="draftLevelMin" type="number" min="1" :max="draftLevelMax || 100" size="sm" @input="setDraftMinLevel"></b-form-input>
            <span>-</span>
            <b-form-input :value="draftLevelMax" type="number" :min="draftLevelMin || 1" max="100" size="sm" @input="setDraftMaxLevel"></b-form-input>
          </div>

          <b-alert v-if="showWarnings && selfHarmingWarnings.length" show variant="warning" class="spawn-self-harm-warning">
            <div class="spawn-warning-title">
              <i class="fa fa-warning mr-1"></i>
              Self-harming move warning
            </div>
            <div v-for="warning in warningRows(selfHarmingWarnings, 'selfHarm', 6)" :key="warning.key" class="spawn-warning-line">
              {{ warning.text }}
            </div>
            <button
              v-if="hiddenWarningCount(selfHarmingWarnings, 'selfHarm', 6)"
              type="button"
              class="spawn-warning-more"
              @click="toggleWarningGroup('selfHarm')"
            >
              {{ warningGroupExpanded('selfHarm') ? 'Show fewer' : '+' + hiddenWarningCount(selfHarmingWarnings, 'selfHarm', 6) + ' more' }}
            </button>
          </b-alert>

          <b-alert v-if="showWarnings && repelWarnings.length" show variant="warning" class="spawn-self-harm-warning">
            <div class="spawn-warning-title">
              <img v-if="!iconErrors.repel" class="spawn-warning-title-icon" :src="repelIconUrl" alt="Repel" @error="markIconError('repel')">
              <i v-else class="fa fa-shield mr-1"></i>
              Repel-trick warning
            </div>
            <div v-for="warning in warningRows(repelWarnings, 'repel', 8)" :key="warning.key" class="spawn-warning-line">
              {{ warning.text }}
            </div>
            <button
              v-if="hiddenWarningCount(repelWarnings, 'repel', 8)"
              type="button"
              class="spawn-warning-more"
              @click="toggleWarningGroup('repel')"
            >
              {{ warningGroupExpanded('repel') ? 'Show fewer' : '+' + hiddenWarningCount(repelWarnings, 'repel', 8) + ' more' }}
            </button>
          </b-alert>

          <div v-if="editMode && showSpawnSectionActions(showLand ? 'land' : 'water')" class="spawn-delete-all-row">
            <b-button size="sm" variant="outline-danger" :disabled="editRows.length === 0" @click="deleteAllEditSpawns">
              <i class="fa fa-trash mr-1"></i>Delete all Spawns
            </b-button>
          </div>

          <section v-if="showLand" class="spawn-section">
            <div class="spawn-section-header">
              <div>
                <i class="fa fa-tree text-success"></i>
                <strong>Land Spawns</strong>
                <span class="badge badge-success">{{ landSpawns.length }}</span>
              </div>
              <div v-if="showSpawnSectionActions('land')" class="spawn-section-actions">
                <b-button
                  v-if="!editMode && canBeginEdit"
                  size="sm"
                  variant="outline-warning"
                  @click="beginEdit"
                >
                  <i class="fa fa-edit mr-1"></i>Edit
                </b-button>
                <template v-if="editMode">
                  <b-button size="sm" variant="success" :disabled="!canAddSpawnInEdit" @click="openAddSpawnModal(displayMode === 'water' ? 'surf' : 'land')">
                    <i class="fa fa-plus mr-1"></i>Add Spawns
                  </b-button>
                  <b-button size="sm" variant="primary" @click="saveDraft">
                    <i class="fa fa-save mr-1"></i>Save
                  </b-button>
                  <b-button size="sm" variant="outline-secondary" @click="cancelEdit">
                    <i class="fa fa-times mr-1"></i>Cancel
                  </b-button>
                </template>
              </div>
            </div>

            <b-alert v-if="showWarnings && landNatureConflictWarnings.length" show variant="warning" class="spawn-self-harm-warning">
              <div class="spawn-warning-title">
                <i class="fa fa-leaf mr-1"></i>
                Land shared nature warning
              </div>
              <div v-for="warning in warningRows(landNatureConflictWarnings, 'nature-land', 8)" :key="warning.key" class="spawn-warning-line">
                {{ warning.text }}
              </div>
              <button
                v-if="hiddenWarningCount(landNatureConflictWarnings, 'nature-land', 8)"
                type="button"
                class="spawn-warning-more"
                @click="toggleWarningGroup('nature-land')"
              >
                {{ warningGroupExpanded('nature-land') ? 'Show fewer' : '+' + hiddenWarningCount(landNatureConflictWarnings, 'nature-land', 8) + ' more' }}
              </button>
            </b-alert>

            <div v-if="landNatureStats.length" class="spawn-nature-summary">
              <button
                v-for="nature in landNatureStats"
                :key="'land-nature-stat-' + nature.name"
                type="button"
                class="spawn-nature-pill"
                :class="{ active: isNatureHighlightActive(nature.name, 'land') }"
                @click="toggleNatureHighlight(nature.name, 'land')"
              >
                <span>{{ nature.name }}</span>
                <strong>{{ formatNaturePercent(nature.percent) }}</strong>
                <small>{{ nature.count }}</small>
              </button>
            </div>

            <div v-if="landSpawns.length === 0" class="spawn-empty-section">No land spawns on this map.</div>
            <div v-else class="spawn-grid">
              <article
                v-for="spawn in landSpawns"
                :key="spawnKey(spawn)"
                class="spawn-card"
                :class="spawnCardClasses(spawn, 'spawn-card-land')"
              >
                <div class="spawn-card-main">
                  <div class="spawn-sprite-column">
                    <div class="spawn-sprite-wrap">
                      <img
                        v-if="!spriteErrors[spawnKey(spawn)]"
                        class="spawn-sprite"
                        :src="pokemonSpriteUrl(spawn.dexID)"
                        :alt="pokemonName(spawn)"
                        :data-fallback-id="pokemonFallbackId(spawn.dexID)"
                        @error="handleSpriteError(spawn, $event)"
                      >
                      <div v-else class="spawn-sprite-fallback">
                        <i class="fa fa-question"></i>
                      </div>
                    </div>
                    <div
                      class="spawn-move-trigger"
                      v-b-tooltip.html.hover
                      :title="moveTooltip(spawn)"
                    >
                      <img
                        v-if="!iconErrors.fighting"
                        class="spawn-type-icon"
                        :src="fightingTypeIconUrl"
                        alt="Moves"
                        @error="markIconError('fighting')"
                      >
                      <span v-else class="spawn-move-text">Moves</span>
                    </div>
                  </div>

                  <div class="spawn-card-body">
                    <div class="spawn-card-heading">
                      <div>
                        <div class="spawn-pokemon" :class="{ 'spawn-membership-name': spawn.memberOnly }">{{ pokemonName(spawn) }}</div>
                        <div class="spawn-subtitle">#{{ spawn.dexID }}</div>
                        <div v-if="pvpUsageRanksForSpawn(spawn).length" class="spawn-pvp-badges">
                          <span
                            v-for="rank in pvpUsageRanksForSpawn(spawn)"
                            :key="spawnKey(spawn) + '-usage-' + rank.rank + '-' + rank.dexID"
                            class="spawn-pvp-badge"
                            v-b-tooltip.hover
                            :title="pvpUsageTooltip(rank)"
                          >
                            <i class="fa fa-trophy mr-1"></i>{{ ordinal(rank.rank) }} Place
                          </span>
                        </div>
                      </div>
                      <div class="spawn-card-heading-actions">
                        <span class="spawn-tier" :class="'tier-group-' + rarityGroupKey(spawn)">
                          {{ rarityGroupLabel(spawn) }}
                        </span>
                        <b-button
                          v-if="editMode"
                          size="sm"
                          variant="outline-danger"
                          @click="deleteEditSpawn(spawn)"
                        >
                          <i class="fa fa-trash"></i>
                        </b-button>
                      </div>
                    </div>

                    <div class="spawn-meta-grid">
                      <div class="spawn-meta-item" :class="{ 'spawn-meta-wide': editMode }">
                        <span>Level Range</span>
                        <div v-if="editMode" class="spawn-inline-level-edit">
                          <b-form-input :value="spawn.minLVL" type="number" min="1" :max="spawn.maxLVL || 100" size="sm" @input="setSpawnMinLevel(spawn, $event)"></b-form-input>
                          <span>-</span>
                          <b-form-input :value="spawn.maxLVL" type="number" :min="spawn.minLVL || 1" max="100" size="sm" @input="setSpawnMaxLevel(spawn, $event)"></b-form-input>
                        </div>
                        <strong v-else class="spawn-level-line">
                          {{ spawn.minLVL }} - {{ spawn.maxLVL }}
                          <span
                            v-if="showWarnings && repelInfoForSpawn(spawn)"
                            class="spawn-repel-icon"
                            v-b-tooltip.html.hover
                            :title="repelTooltip(spawn)"
                          >
                            <img v-if="!iconErrors.repel" :src="repelIconUrl" alt="Repel" @error="markIconError('repel')">
                            <i v-else class="fa fa-shield"></i>
                          </span>
                        </strong>
                      </div>
                      <div class="spawn-meta-item">
                        <span>Rarity</span>
                        <template v-if="editMode">
                          <b-form-select
                            :value="rarityTier(spawn)"
                            :options="addSpawnRarityOptions"
                            size="sm"
                            class="bg-dark text-white border-secondary mb-1"
                            @input="updateSpawnRarityFromTier(spawn, $event)"
                          ></b-form-select>
                          <b-form-input :value="rawRarity(spawn)" type="number" step="0.0001" min="0" size="sm" @input="setSpawnRarity(spawn, $event)"></b-form-input>
                        </template>
                        <template v-else>
                          <strong>{{ rarityTier(spawn) }}</strong>
                          <small>{{ rawRarity(spawn) }}</small>
                        </template>
                      </div>
                      <div class="spawn-meta-item spawn-meta-wide">
                        <span>{{ editMode ? 'Item Name' : 'Item Drop' }}</span>
                        <template v-if="editMode">
                          <b-form-input
                            :value="itemNameInputValue(spawn)"
                            list="spawn-item-options"
                            size="sm"
                            class="bg-dark text-white border-secondary mb-1"
                            :state="itemInputState(spawn)"
                            @input="setSpawnItemName(spawn, $event)"
                          ></b-form-input>
                          <b-form-input
                            :value="Number(spawn.itemRarity || 0) * 100"
                            type="number"
                            step="0.1"
                            min="0"
                            size="sm"
                            :disabled="!hasItem(spawn)"
                            @input="setSpawnItemDropChance(spawn, $event)"
                          ></b-form-input>
                        </template>
                        <template v-else>
                          <strong>{{ itemName(spawn) }}</strong>
                          <small v-if="hasItem(spawn)">{{ itemDropLabel(spawn) }}</small>
                        </template>
                      </div>
                    </div>

                    <div class="spawn-flags">
                      <label class="spawn-check mb-0">
                        <input v-model="spawn.memberOnly" type="checkbox" :disabled="!editMode">
                        <span>Membership</span>
                      </label>
                    </div>

                    <div v-if="formLabels(spawn).length" class="spawn-forms">
                      <span v-for="label in formLabels(spawn)" :key="spawnKey(spawn) + '-form-' + label" class="spawn-form-badge">
                        {{ label }}
                      </span>
                    </div>

                    <div class="spawn-time-grid">
                      <div v-for="period in timePeriods" :key="spawnKey(spawn) + '-' + period.key" class="spawn-time-cell" :class="{ active: isTimeActive(spawn, period.key) }">
                        <div class="spawn-time-label">{{ period.label }}</div>
                        <input
                          type="checkbox"
                          :disabled="!editMode"
                          :checked="isTimeActive(spawn, period.key)"
                          @change="setSpawnTimeActive(spawn, period.key, $event.target.checked)"
                        >
                        <div class="spawn-time-percent">{{ probabilityLabel(spawn, period.key) }}</div>
                      </div>
                    </div>
                    <div v-if="fishingChanceRows(spawn).length" class="spawn-fishing-chances">
                      <div v-for="row in fishingChanceRows(spawn)" :key="spawnKey(spawn) + '-fish-' + row.key" class="spawn-fishing-chance-row">
                        <span class="spawn-fishing-rod-label">
                          <img class="spawn-item-icon" :src="row.icon" :alt="row.label">
                          {{ row.label }}
                        </span>
                        <span v-for="chance in row.chances" :key="row.key + '-' + chance.key">{{ chance.label }} {{ chance.value }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </section>

          <section v-if="showWater" class="spawn-section">
            <div class="spawn-section-header">
              <div>
                <i class="fa fa-tint text-info"></i>
                <strong>Water Spawns</strong>
                <span class="badge badge-info">{{ waterSpawns.length }}</span>
              </div>
              <div v-if="showSpawnSectionActions('water')" class="spawn-section-actions">
                <b-button
                  v-if="!editMode && canBeginEdit"
                  size="sm"
                  variant="outline-warning"
                  @click="beginEdit"
                >
                  <i class="fa fa-edit mr-1"></i>Edit
                </b-button>
                <template v-if="editMode">
                  <b-button size="sm" variant="success" :disabled="!canAddSpawnInEdit" @click="openAddSpawnModal(displayMode === 'water' ? 'surf' : 'land')">
                    <i class="fa fa-plus mr-1"></i>Add Spawns
                  </b-button>
                  <b-button size="sm" variant="primary" @click="saveDraft">
                    <i class="fa fa-save mr-1"></i>Save
                  </b-button>
                  <b-button size="sm" variant="outline-secondary" @click="cancelEdit">
                    <i class="fa fa-times mr-1"></i>Cancel
                  </b-button>
                </template>
              </div>
            </div>

            <b-alert v-if="showWarnings && waterNatureConflictWarnings.length" show variant="warning" class="spawn-self-harm-warning">
              <div class="spawn-warning-title">
                <i class="fa fa-leaf mr-1"></i>
                Water shared nature warning
              </div>
              <div v-for="warning in warningRows(waterNatureConflictWarnings, 'nature-water', 8)" :key="warning.key" class="spawn-warning-line">
                {{ warning.text }}
              </div>
              <button
                v-if="hiddenWarningCount(waterNatureConflictWarnings, 'nature-water', 8)"
                type="button"
                class="spawn-warning-more"
                @click="toggleWarningGroup('nature-water')"
              >
                {{ warningGroupExpanded('nature-water') ? 'Show fewer' : '+' + hiddenWarningCount(waterNatureConflictWarnings, 'nature-water', 8) + ' more' }}
              </button>
            </b-alert>

            <div v-if="waterNatureStats.length" class="spawn-nature-summary">
              <button
                v-for="nature in waterNatureStats"
                :key="'water-nature-stat-' + nature.name"
                type="button"
                class="spawn-nature-pill"
                :class="{ active: isNatureHighlightActive(nature.name, 'water') }"
                @click="toggleNatureHighlight(nature.name, 'water')"
              >
                <span>{{ nature.name }}</span>
                <strong>{{ formatNaturePercent(nature.percent) }}</strong>
                <small>{{ nature.count }}</small>
              </button>
            </div>

            <div v-if="waterSpawns.length === 0" class="spawn-empty-section">No water spawns on this map.</div>
            <div v-else class="spawn-grid">
              <article
                v-for="spawn in waterSpawns"
                :key="spawnKey(spawn)"
                class="spawn-card"
                :class="spawnCardClasses(spawn, 'spawn-card-water')"
              >
                <div class="spawn-card-main">
                  <div class="spawn-sprite-column">
                    <div class="spawn-sprite-wrap">
                      <img
                        v-if="!spriteErrors[spawnKey(spawn)]"
                        class="spawn-sprite"
                        :src="pokemonSpriteUrl(spawn.dexID)"
                        :alt="pokemonName(spawn)"
                        :data-fallback-id="pokemonFallbackId(spawn.dexID)"
                        @error="handleSpriteError(spawn, $event)"
                      >
                      <div v-else class="spawn-sprite-fallback">
                        <i class="fa fa-question"></i>
                      </div>
                    </div>
                    <div
                      class="spawn-move-trigger"
                      v-b-tooltip.html.hover
                      :title="moveTooltip(spawn)"
                    >
                      <img
                        v-if="!iconErrors.fighting"
                        class="spawn-type-icon"
                        :src="fightingTypeIconUrl"
                        alt="Moves"
                        @error="markIconError('fighting')"
                      >
                      <span v-else class="spawn-move-text">Moves</span>
                    </div>
                  </div>

                  <div class="spawn-card-body">
                    <div class="spawn-card-heading">
                      <div>
                        <div class="spawn-pokemon" :class="{ 'spawn-membership-name': spawn.memberOnly }">{{ pokemonName(spawn) }}</div>
                        <div class="spawn-subtitle">#{{ spawn.dexID }}</div>
                        <div v-if="pvpUsageRanksForSpawn(spawn).length" class="spawn-pvp-badges">
                          <span
                            v-for="rank in pvpUsageRanksForSpawn(spawn)"
                            :key="spawnKey(spawn) + '-usage-' + rank.rank + '-' + rank.dexID"
                            class="spawn-pvp-badge"
                            v-b-tooltip.hover
                            :title="pvpUsageTooltip(rank)"
                          >
                            <i class="fa fa-trophy mr-1"></i>{{ ordinal(rank.rank) }} Place
                          </span>
                        </div>
                      </div>
                      <div class="spawn-card-heading-actions">
                        <span class="spawn-tier" :class="'tier-group-' + rarityGroupKey(spawn)">
                          {{ rarityGroupLabel(spawn) }}
                        </span>
                        <b-button
                          v-if="editMode"
                          size="sm"
                          variant="outline-danger"
                          @click="deleteEditSpawn(spawn)"
                        >
                          <i class="fa fa-trash"></i>
                        </b-button>
                      </div>
                    </div>

                    <div class="spawn-meta-grid">
                      <div class="spawn-meta-item" :class="{ 'spawn-meta-wide': editMode }">
                        <span>Level Range</span>
                        <div v-if="editMode" class="spawn-inline-level-edit">
                          <b-form-input :value="spawn.minLVL" type="number" min="1" :max="spawn.maxLVL || 100" size="sm" @input="setSpawnMinLevel(spawn, $event)"></b-form-input>
                          <span>-</span>
                          <b-form-input :value="spawn.maxLVL" type="number" :min="spawn.minLVL || 1" max="100" size="sm" @input="setSpawnMaxLevel(spawn, $event)"></b-form-input>
                        </div>
                        <strong v-else class="spawn-level-line">
                          {{ spawn.minLVL }} - {{ spawn.maxLVL }}
                          <span
                            v-if="showWarnings && repelInfoForSpawn(spawn)"
                            class="spawn-repel-icon"
                            v-b-tooltip.html.hover
                            :title="repelTooltip(spawn)"
                          >
                            <img v-if="!iconErrors.repel" :src="repelIconUrl" alt="Repel" @error="markIconError('repel')">
                            <i v-else class="fa fa-shield"></i>
                          </span>
                        </strong>
                      </div>
                      <div class="spawn-meta-item">
                        <span>Rarity</span>
                        <template v-if="editMode">
                          <b-form-select
                            :value="rarityTier(spawn)"
                            :options="addSpawnRarityOptions"
                            size="sm"
                            class="bg-dark text-white border-secondary mb-1"
                            @input="updateSpawnRarityFromTier(spawn, $event)"
                          ></b-form-select>
                          <b-form-input :value="rawRarity(spawn)" type="number" step="0.0001" min="0" size="sm" @input="setSpawnRarity(spawn, $event)"></b-form-input>
                        </template>
                        <template v-else>
                          <strong>{{ rarityTier(spawn) }}</strong>
                          <small>{{ rawRarity(spawn) }}</small>
                        </template>
                      </div>
                      <div class="spawn-meta-item spawn-meta-wide">
                        <span>{{ editMode ? 'Item Name' : 'Item Drop' }}</span>
                        <template v-if="editMode">
                          <b-form-input
                            :value="itemNameInputValue(spawn)"
                            list="spawn-item-options"
                            size="sm"
                            class="bg-dark text-white border-secondary mb-1"
                            :state="itemInputState(spawn)"
                            @input="setSpawnItemName(spawn, $event)"
                          ></b-form-input>
                          <b-form-input
                            :value="Number(spawn.itemRarity || 0) * 100"
                            type="number"
                            step="0.1"
                            min="0"
                            size="sm"
                            :disabled="!hasItem(spawn)"
                            @input="setSpawnItemDropChance(spawn, $event)"
                          ></b-form-input>
                        </template>
                        <template v-else>
                          <strong>{{ itemName(spawn) }}</strong>
                          <small v-if="hasItem(spawn)">{{ itemDropLabel(spawn) }}</small>
                        </template>
                      </div>
                    </div>

                    <div class="spawn-flags">
                      <label class="spawn-check mb-0">
                        <input v-model="spawn.memberOnly" type="checkbox" :disabled="!editMode">
                        <span>Membership</span>
                      </label>
                      <label class="spawn-check mb-0">
                        <span class="spawn-fishing-icon" title="Fishing">
                          <img v-if="rodIconForSpawn(spawn)" class="spawn-item-icon" :src="rodIconForSpawn(spawn)" :alt="rodLabel(spawn)">
                          <i v-else class="fa fa-tint"></i>
                        </span>
                        <input type="checkbox" disabled :checked="isFishable(spawn)">
                        <span>{{ rodLabel(spawn) }}</span>
                      </label>
                      <span v-if="isFishingOnly(spawn)" class="spawn-fishing-only">Fishing only</span>
                    </div>

                    <div v-if="editMode" class="spawn-edit-grid">
                      <label>
                        <span>Required Rod</span>
                        <b-form-select
                          v-model.number="spawn.required_rod"
                          :options="[
                            { value: 0, text: 'Not fishable' },
                            { value: 1, text: 'Old Rod+' },
                            { value: 2, text: 'Good Rod+' },
                            { value: 3, text: 'Super Rod' }
                          ]"
                          size="sm"
                          class="bg-dark text-white border-secondary"
                        ></b-form-select>
                      </label>
                      <label class="spawn-edit-check">
                        <input :checked="spawn.fishOnly === undefined ? isFishingOnly(spawn) : spawn.fishOnly" type="checkbox" @change="setSpawnFishOnly(spawn, $event.target.checked)">
                        <span>Fish-only</span>
                      </label>
                    </div>

                    <div v-if="formLabels(spawn).length" class="spawn-forms">
                      <span v-for="label in formLabels(spawn)" :key="spawnKey(spawn) + '-form-' + label" class="spawn-form-badge">
                        {{ label }}
                      </span>
                    </div>

                    <div class="spawn-time-grid">
                      <div v-for="period in timePeriods" :key="spawnKey(spawn) + '-' + period.key" class="spawn-time-cell" :class="{ active: isTimeActive(spawn, period.key) }">
                        <div class="spawn-time-label">{{ period.label }}</div>
                        <input
                          type="checkbox"
                          :disabled="!editMode"
                          :checked="isTimeActive(spawn, period.key)"
                          @change="setSpawnTimeActive(spawn, period.key, $event.target.checked)"
                        >
                        <div class="spawn-time-percent">{{ probabilityLabel(spawn, period.key) }}</div>
                      </div>
                    </div>
                    <div v-if="fishingChanceRows(spawn).length" class="spawn-fishing-chances">
                      <div v-for="row in fishingChanceRows(spawn)" :key="spawnKey(spawn) + '-fish-' + row.key" class="spawn-fishing-chance-row">
                        <span class="spawn-fishing-rod-label">
                          <img class="spawn-item-icon" :src="row.icon" :alt="row.label">
                          {{ row.label }}
                        </span>
                        <span v-for="chance in row.chances" :key="row.key + '-' + chance.key">{{ chance.label }} {{ chance.value }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </section>
        </template>
      </main>
    </div>

    <b-modal
      v-model="newMapModal.visible"
      title="New Spawn Map"
      ok-title="Create"
      ok-variant="success"
      @ok.prevent="createNewMapDraft"
    >
      <b-form-group label="Map Name">
        <b-form-input v-model.trim="newMapModal.mapName" class="bg-dark text-white border-secondary"></b-form-input>
      </b-form-group>
    </b-modal>

    <b-modal
      v-model="addSpawnModal.visible"
      size="xl"
      modal-class="spawn-add-modal"
      title="Add Spawn"
      ok-title="Add Selected"
      ok-variant="success"
      :ok-disabled="selectedAddSpawnCount === 0 || !canUseAddSpawnLevelRange"
      @ok.prevent="addSelectedSpawns"
    >
      <div class="spawn-add-layout">
        <aside class="spawn-add-panel spawn-add-filter-panel">
          <div class="spawn-add-control-block">
            <div class="spawn-add-control-title">Area</div>
            <b-button-group size="sm" class="spawn-add-area-toggle">
              <b-button :variant="addSpawnModal.area === 'land' ? 'success' : 'outline-success'" @click="addSpawnModal.area = 'land'">
                <i class="fa fa-tree mr-1"></i>Land
              </b-button>
              <b-button :variant="addSpawnModal.area === 'surf' ? 'primary' : 'outline-primary'" @click="addSpawnModal.area = 'surf'">
                <i class="fa fa-tint mr-1"></i>Water
              </b-button>
            </b-button-group>
          </div>

          <div class="spawn-add-control-block">
            <div class="spawn-add-control-title">Level Range</div>
            <div class="spawn-add-level-edit">
              <b-form-input :value="draftLevelMin" type="number" min="1" :max="draftLevelMax || 100" size="sm" class="bg-dark text-white border-secondary" @input="setDraftMinLevel"></b-form-input>
              <span>-</span>
              <b-form-input :value="draftLevelMax" type="number" :min="draftLevelMin || 1" max="100" size="sm" class="bg-dark text-white border-secondary" @input="setDraftMaxLevel"></b-form-input>
            </div>
            <div v-if="!canUseAddSpawnLevelRange" class="spawn-add-level-warning">
              Enter a level range before adding spawns.
            </div>
          </div>

          <div class="spawn-add-filter-actions">
            <b-button size="sm" variant="outline-secondary" @click="resetAddSpawnFilters">
              <i class="fa fa-times mr-1"></i>Clear Filters
            </b-button>
          </div>

          <section
            v-for="filter in addSpawnFilterGroups"
            :key="'add-filter-' + filter.key"
            class="spawn-add-filter-group"
            :class="{ disabled: !isAddSpawnFilterActive(filter.key) }"
          >
            <label class="spawn-add-filter-heading">
              <input
                type="checkbox"
                :checked="isAddSpawnFilterActive(filter.key)"
                @change="setAddSpawnFilterActive(filter.key, $event.target.checked)"
              >
              <span>{{ filter.label }}</span>
              <small>{{ addSpawnFilterSelectedCount(filter.key) }}</small>
            </label>
            <div v-if="filter.options.length" class="spawn-add-filter-options">
              <label
                v-for="option in filter.options"
                :key="'add-filter-' + filter.key + '-' + option.value"
                class="spawn-add-filter-option"
              >
                <input
                  type="checkbox"
                  :disabled="!isAddSpawnFilterActive(filter.key)"
                  :checked="isAddSpawnFilterValueSelected(filter.key, option.value)"
                  @change="toggleAddSpawnFilterValue(filter.key, option.value, $event.target.checked)"
                >
                <span>{{ option.text }}</span>
              </label>
            </div>
          </section>
        </aside>

        <section class="spawn-add-panel spawn-add-available-panel">
          <div class="spawn-add-toolbar">
            <b-form-input
              v-model.trim="addSpawnModal.search"
              size="sm"
              placeholder="Search Pokemon..."
              class="bg-dark text-white border-secondary"
            ></b-form-input>
            <b-form-select
              v-model="addSpawnModal.sortKey"
              :options="addSpawnSortOptions"
              size="sm"
              class="bg-dark text-white border-secondary spawn-add-sort"
            ></b-form-select>
            <b-button size="sm" variant="outline-info" @click="addSpawnModal.sortDirection = addSpawnModal.sortDirection === 'asc' ? 'desc' : 'asc'">
              <i class="fa" :class="addSpawnModal.sortDirection === 'asc' ? 'fa-sort-amount-asc' : 'fa-sort-amount-desc'"></i>
            </b-button>
          </div>

          <div class="spawn-add-result-summary">
            {{ availableAddSpawnPokemon.length }} available - {{ selectedAddSpawnCount }} selected
          </div>

          <div class="spawn-add-grid">
            <button
              v-for="pokemon in availableAddSpawnPokemon"
              :key="'add-pokemon-' + pokemon.dexID"
              type="button"
              draggable="true"
              class="spawn-add-pokemon"
              @dragstart="startAddSpawnDrag(pokemon, $event)"
              @dragend="endAddSpawnDrag"
            >
              <span class="spawn-add-sprite">
                <img
                  v-if="!spriteErrors['add-' + pokemon.dexID]"
                  :src="pokemonSpriteUrl(pokemon.dexID)"
                  :alt="pokemon.name"
                  :data-fallback-id="pokemonFallbackId(pokemon.dexID)"
                  @error="handleAddSpawnSpriteError(pokemon, $event)"
                >
                <i v-else class="fa fa-question"></i>
              </span>
              <span class="spawn-add-name">{{ pokemon.name }}</span>
              <small>#{{ pokemon.dexID }}</small>
            </button>
          </div>
        </section>

        <aside
          class="spawn-add-panel spawn-add-selected-panel"
          :class="{ dragging: addSpawnModal.draggingDexID }"
          @dragover.prevent
          @drop="dropAddSpawnPokemon"
        >
          <div class="spawn-add-selected-title">
            <span>Selected Pokemon</span>
            <strong>{{ selectedAddSpawnCount }}</strong>
          </div>
          <div v-if="selectedAddSpawnPokemon.length === 0" class="spawn-add-drop-empty">
            No Pokemon selected.
          </div>
          <div v-else class="spawn-add-selected-list">
            <div
              v-for="pokemon in selectedAddSpawnPokemon"
              :key="'selected-add-pokemon-' + pokemon.dexID"
              class="spawn-add-selected-row"
            >
              <span class="spawn-add-selected-sprite">
                <img
                  v-if="!spriteErrors['add-selected-' + pokemon.dexID]"
                  :src="pokemonSpriteUrl(pokemon.dexID)"
                  :alt="pokemon.name"
                  :data-fallback-id="pokemonFallbackId(pokemon.dexID)"
                  @error="handleAddSpawnSelectedSpriteError(pokemon, $event)"
                >
                <i v-else class="fa fa-question"></i>
              </span>
              <span class="spawn-add-selected-name">
                <strong>{{ pokemon.name }}</strong>
                <small>#{{ pokemon.dexID }} - {{ addSpawnPokemonTierLabel(pokemon.dexID) }}</small>
              </span>
              <button type="button" class="spawn-add-remove" @click="removeAddSpawnSelection(pokemon.dexID)">
                <i class="fa fa-times"></i>
              </button>
            </div>
          </div>
        </aside>
      </div>
    </b-modal>

    <datalist id="spawn-item-options">
      <option v-for="item in itemOptions" :key="'spawn-item-option-' + item.id" :value="item.name" :label="'#' + item.id"></option>
    </datalist>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import MovesIcon from '@/assets/Moves.png'
import Monsters from '@/views/pokemon/_monsters'

const TIME_PERIODS = [
  { key: 'morning', label: 'Morning', start: 4, end: 10 },
  { key: 'day', label: 'Day', start: 10, end: 20 },
  { key: 'night', label: 'Night', start: 20, end: 4 }
]

const TIME_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'morning', label: 'Morning' },
  { key: 'day', label: 'Day' },
  { key: 'night', label: 'Night' }
]

const SORT_OPTIONS = [
  { value: 'name', text: 'Name' },
  { value: 'dexID', text: 'DexID' },
  { value: 'rarity', text: 'Rarity' }
]

const ADD_SPAWN_SORT_OPTIONS = [
  { value: 'dexID', text: 'DexID' },
  { value: 'name', text: 'Name' },
  { value: 'rarity', text: 'Rarity' }
]

const MAP_SEARCH_MODE_OPTIONS = [
  { value: 'map', text: 'Map' },
  { value: 'pokemon', text: 'Pokemon' },
  { value: 'region', text: 'Region' }
]

const WORKFLOW_VIEW_OPTIONS = [
  { value: 'live', text: 'Live' },
  { value: 'wip', text: 'Workspace' },
  { value: 'review', text: 'Review' },
  { value: 'ready', text: 'Ready for Upload' }
]

const EVOLUTION_STAGE_OPTIONS = [
  { value: 'baby', text: 'Baby Stage' },
  { value: 'middle', text: 'Middle Evolution' },
  { value: 'fully', text: 'Fully Evolved' }
]

const REGION_FILTERS = [
  { value: 'kanto', text: 'Kanto', ranges: [[1, 151]] },
  { value: 'johto', text: 'Johto', ranges: [[152, 251]] },
  { value: 'hoenn', text: 'Hoenn', ranges: [[252, 386]] },
  { value: 'sinnoh', text: 'Sinnoh', ranges: [[387, 493]] },
  { value: 'einall', text: 'Einall', ranges: [[494, 649]] },
  { value: 'kalos', text: 'Kalos', ranges: [[650, 721]] },
  { value: 'alola', text: 'Alola', ranges: [[722, 809], [3057, 3074]] },
  { value: 'galar', text: 'Galar', ranges: [[810, 905], [3112, 3122], [3128, 3130], [3243, 3248]] },
  { value: 'hisui', text: 'Hisui', ranges: [[3224, 3239]] },
  { value: 'paldea', text: 'Paldea', ranges: [[906, 1025], [3249, 3252]] }
]

const POKEAPI_ITEM_ICON_BASE = 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/items/'
const REPEL_ICON_URL = POKEAPI_ITEM_ICON_BASE + 'repel.png'
const FIGHTING_TYPE_ICON_URL = MovesIcon

const FISHING_RODS = [
  { key: 'old', label: 'Old Rod', requiredRod: 1, icon: POKEAPI_ITEM_ICON_BASE + 'old-rod.png' },
  { key: 'good', label: 'Good Rod', requiredRod: 2, icon: POKEAPI_ITEM_ICON_BASE + 'good-rod.png' },
  { key: 'super', label: 'Super Rod', requiredRod: 3, icon: POKEAPI_ITEM_ICON_BASE + 'super-rod.png' }
]

const SPAWN_EDITOR_PREFS_KEY = 'spawn-editor-preferences'
const SANDSTORM_SAFE_TYPES = ['rock', 'steel', 'ground']

const LAND_RARITIES = [
  { tier: 'T1', value: 0.03 },
  { tier: 'T2', value: 0.01 },
  { tier: 'T3', value: 0.008 },
  { tier: 'T4', value: 0.006 },
  { tier: 'T5', value: 0.004 },
  { tier: 'T6', value: 0.002 },
  { tier: 'T7', value: 0.0009 },
  { tier: 'T8', value: 0.0007 },
  { tier: 'T9', value: 0.0005 },
  { tier: 'T9.5', value: 0.0003 },
  { tier: 'T10', value: 0 }
]

const WATER_RARITIES = [
  { tier: 'T1', value: 0.03 },
  { tier: 'T2', value: 0.01 },
  { tier: 'T3', value: 0.008 },
  { tier: 'T4', value: 0.006 },
  { tier: 'T5', value: 0.005 },
  { tier: 'T6', value: 0.003 },
  { tier: 'T7', value: 0.001 },
  { tier: 'T8', value: 0.0008 },
  { tier: 'T9', value: 0.0006 },
  { tier: 'T9.5', value: 0.0004 },
  { tier: 'T10', value: 0 }
]

const MONSTER_BY_ID = Object.create(null)
const MONSTER_BY_NAME = Object.create(null)
Monsters.forEach(monster => {
  MONSTER_BY_ID[Number(monster.ID)] = monster
  MONSTER_BY_NAME[String(monster.Name || '').toLowerCase()] = monster
})

export default {
  name: 'SpawnEditor',
  data () {
    return {
      activeMap: '',
      activeBatchId: null,
      activeNatureHighlight: '',
      activeNatureHighlightArea: '',
      activeTimeFilter: 'all',
      addSpawnModal: {
        visible: false,
        area: 'land',
        search: '',
        sortKey: 'dexID',
        sortDirection: 'asc',
        selected: {},
        selectedOrder: [],
        draggingDexID: null,
        activeFilters: {
          types: true,
          rarities: true,
          habitats: true,
          evolutionStages: true,
          regions: true,
          events: true,
          natures: true,
          topUsage: false
        },
        filters: {
          types: [],
          rarities: [],
          habitats: [],
          evolutionStages: [],
          regions: [],
          events: [],
          natures: []
        }
      },
      displayMode: 'stacked',
      draftLevelMax: null,
      draftLevelMin: null,
      editMode: false,
      editRows: [],
      expandedWarningGroups: {
        'nature-land': false,
        'nature-water': false,
        repel: false,
        selfHarm: false
      },
      fightingTypeIconUrl: FIGHTING_TYPE_ICON_URL,
      iconErrors: {},
      loading: false,
      loadingTimer: null,
      mapSearchMode: 'map',
      mapSearchModeOptions: MAP_SEARCH_MODE_OPTIONS,
      mapSearch: '',
      membershipActive: false,
      moveCache: Object.create(null),
      newMapModal: {
        visible: false,
        mapName: ''
      },
      repelIconUrl: REPEL_ICON_URL,
      repelMapCache: Object.create(null),
      selectedSearchEntity: null,
      showWarnings: false,
      sortDirection: 'asc',
      sortKey: 'name',
      sortOptions: SORT_OPTIONS,
      spriteErrors: {},
      timeFilters: TIME_FILTERS,
      timePeriods: TIME_PERIODS,
      workflowView: 'live'
    }
  },
  computed: {
    ...mapGetters({
      spawns: 'spawns',
      spawnsUpdatedAt: 'spawnsUpdatedAt',
      formMapping: 'formMapping',
      referenceData: 'spawnReferenceData',
      referenceLookups: 'spawnReferenceLookups',
      referenceDataUpdatedAt: 'spawnReferenceDataUpdatedAt',
      spawnMoves: 'spawnMoves',
      spawnPokemonCatalog: 'spawnPokemonCatalog',
      spawnUsageRanks: 'spawnUsageRanks',
      workflowBatches: 'spawnWorkflowBatches',
      workflowCanUploadReady: 'spawnWorkflowCanUploadReady',
      workflowUpdatedAt: 'spawnWorkflowUpdatedAt'
    }),
    workflowBatchCounts () {
      return (this.workflowBatches || []).reduce((counts, batch) => {
        const status = String(batch.status || '')
        counts[status] = (counts[status] || 0) + 1
        return counts
      }, Object.create(null))
    },
    visibleWorkflowViewOptions () {
      return WORKFLOW_VIEW_OPTIONS.filter(option => {
        if (option.value === 'live' || option.value === 'wip') return true
        return Number(this.workflowBatchCounts[option.value] || 0) > 0
      })
    },
    canBeginEdit () {
      if (this.editMode || !this.activeMap) return false
      return this.workflowView === 'live' || (this.workflowView === 'wip' && this.activeBatch && this.activeBatch.isOwner)
    },
    liveSpawnsByMap () {
      const grouped = Object.create(null)
      ;(this.spawns || []).forEach(spawn => {
        const name = spawn.map || 'Unknown'
        if (!grouped[name]) grouped[name] = []
        grouped[name].push(spawn)
      })
      return grouped
    },
    spawnsByMap () {
      const grouped = Object.create(null)
      ;(this.displayRows || []).forEach(spawn => {
        const name = spawn.map || 'Unknown'
        if (!grouped[name]) grouped[name] = []
        grouped[name].push(spawn)
      })
      return grouped
    },
    spawnsByDexID () {
      const grouped = Object.create(null)
      ;(this.spawns || []).forEach(spawn => {
        const dexID = Number(spawn.dexID || 0)
        if (!dexID) return
        if (!grouped[dexID]) grouped[dexID] = []
        grouped[dexID].push(spawn)
      })
      return grouped
    },
    displayRows () {
      if (this.workflowView === 'live') return this.spawns || []
      if (this.activeBatch) return this.activeBatch.spawns || []
      return []
    },
    maps () {
      if (this.workflowView !== 'live') {
        return this.currentWorkflowBatches.map(batch => {
          const rows = batch.spawns || []
          const map = {
            name: batch.mapName || 'Unknown',
            batchID: batch.id,
            creatorName: batch.creatorName,
            status: batch.status,
            land: 0,
            water: 0,
            total: rows.length
          }
          rows.forEach(spawn => {
            if (this.isWaterSpawn(spawn)) {
              map.water++
            } else {
              map.land++
            }
          })
          return map
        })
      }
      const names = Object.keys(this.liveSpawnsByMap)
      this.referenceMapNames.forEach(name => {
        if (names.indexOf(name) === -1) names.push(name)
      })
      return names
        .map(name => {
          const rows = this.liveSpawnsByMap[name] || []
          const map = { name: name, land: 0, water: 0, total: rows.length }
          rows.forEach(spawn => {
            if (this.isWaterSpawn(spawn)) {
              map.water++
            } else {
              map.land++
            }
          })
          return map
        })
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
    },
    referenceMapNames () {
      const regions = this.referenceData && this.referenceData.mapRegions ? this.referenceData.mapRegions : {}
      const seen = Object.create(null)
      Object.keys(regions).forEach(region => {
        ;(regions[region] || []).forEach(mapName => {
          const name = String(mapName || '').trim()
          if (name) seen[name] = true
        })
      })
      return Object.keys(seen).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
    },
    mapByName () {
      return this.maps.reduce((result, map) => {
        result[map.name] = map
        return result
      }, Object.create(null))
    },
    mapByLowerName () {
      return this.maps.reduce((result, map) => {
        result[map.name.toLowerCase()] = map
        return result
      }, Object.create(null))
    },
    pokemonOptionsByName () {
      return this.pokemonOptions.reduce((result, option) => {
        result[option.name.toLowerCase()] = option
        return result
      }, Object.create(null))
    },
    regionOptionsByName () {
      return this.regionOptions.reduce((result, option) => {
        result[option.name.toLowerCase()] = option
        return result
      }, Object.create(null))
    },
    filteredMaps () {
      const search = String(this.mapSearch || '').toLowerCase()
      if (!search) return this.maps
      return this.maps.filter(map => map.name.toLowerCase().indexOf(search) !== -1)
    },
    sidebarSummary () {
      if (this.workflowView !== 'live') {
        return this.currentWorkflowBatches.length + ' batches'
      }
      if (this.mapSearchMode === 'pokemon') {
        if (this.selectedSearchEntity) return this.sidebarItems.length + ' maps'
        if (this.exactPokemonSearchMatch()) return this.sidebarItems.length + ' maps'
        return this.pokemonOptions.length + ' Pokemon'
      }
      if (this.mapSearchMode === 'region') {
        if (this.selectedSearchEntity) return this.sidebarItems.length + ' maps'
        if (this.exactRegionSearchMatch()) return this.sidebarItems.length + ' maps'
        if (String(this.mapSearch || '').trim()) {
          const mapCount = this.regionSidebarItems.filter(item => item.type === 'regionMap').length
          return mapCount ? mapCount + ' maps' : this.regionSidebarItems.length + ' regions'
        }
        return this.regionOptions.length + ' regions'
      }
      return this.maps.length + ' with spawns'
    },
    mapSearchPlaceholder () {
      if (this.mapSearchMode === 'pokemon') return this.selectedSearchEntity ? 'Filter maps...' : 'Search Pokemon...'
      if (this.mapSearchMode === 'region') return this.selectedSearchEntity ? 'Filter maps...' : 'Search region...'
      return 'Search map...'
    },
    selectedSearchEntityLabel () {
      if (!this.selectedSearchEntity) return ''
      if (this.selectedSearchEntity.type === 'pokemon') return 'Pokemon: ' + this.selectedSearchEntity.name
      if (this.selectedSearchEntity.type === 'region') return 'Region: ' + this.selectedSearchEntity.name
      return ''
    },
    sidebarItems () {
      if (this.mapSearchMode === 'pokemon') {
        if (this.selectedSearchEntity && this.selectedSearchEntity.type === 'pokemon') {
          return this.pokemonMapItems(this.selectedSearchEntity)
        }
        const exactPokemon = this.exactPokemonSearchMatch()
        if (exactPokemon) {
          return this.pokemonMapItems({ type: 'pokemon', dexID: exactPokemon.dexID, name: exactPokemon.name })
        }
        return this.filteredPokemonOptions
      }
      if (this.mapSearchMode === 'region') {
        if (this.selectedSearchEntity && this.selectedSearchEntity.type === 'region') {
          return this.regionMapItems(this.selectedSearchEntity)
        }
        const exactRegion = this.exactRegionSearchMatch()
        if (exactRegion) {
          return this.regionMapItems({ type: 'region', name: exactRegion.name })
        }
        return this.regionSidebarItems
      }
      return this.filteredMaps.map(map => ({ type: 'map', map }))
    },
    pokemonOptions () {
      return Object.keys(this.spawnsByDexID)
        .map(dexID => {
          const rows = this.spawnsByDexID[dexID] || []
          const maps = Object.create(null)
          rows.forEach(spawn => {
            maps[spawn.map || 'Unknown'] = true
          })
          return {
            type: 'pokemonOption',
            dexID: Number(dexID),
            name: rows.length ? this.pokemonName(rows[0]) : 'Unknown Pokemon',
            mapCount: Object.keys(maps).length,
            memberOnly: rows.length > 0 && rows.every(spawn => spawn.memberOnly),
            repelInfo: null
          }
        })
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
    },
    filteredPokemonOptions () {
      const search = String(this.mapSearch || '').toLowerCase()
      if (!search) return this.pokemonOptions
      return this.pokemonOptions.filter(option => option.name.toLowerCase().indexOf(search) !== -1 || String(option.dexID).indexOf(search) !== -1)
    },
    regionOptions () {
      const regions = this.referenceData && this.referenceData.mapRegions ? this.referenceData.mapRegions : {}
      return Object.keys(regions)
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
        .map(region => {
          const regionMaps = regions[region] || []
          const mapNames = regionMaps.filter(mapName => this.mapByLowerName[String(mapName).toLowerCase()])
          return {
            type: 'regionOption',
            name: region,
            maps: mapNames,
            mapCount: mapNames.length
          }
        })
    },
    filteredRegionOptions () {
      const search = String(this.mapSearch || '').toLowerCase()
      if (!search) return this.regionOptions
      return this.regionOptions.filter(option => option.name.toLowerCase().indexOf(search) !== -1)
    },
    regionSidebarItems () {
      const search = String(this.mapSearch || '').trim().toLowerCase()
      if (!search) return this.regionOptions
      const items = []
      this.regionOptions.forEach(region => {
        const regionMatches = region.name.toLowerCase().indexOf(search) !== -1
        const matchingMaps = region.maps
          .map(mapName => this.mapByLowerName[String(mapName).toLowerCase()])
          .filter(map => map && map.name.toLowerCase().indexOf(search) !== -1)
        if (!regionMatches && !matchingMaps.length) return
        items.push(Object.assign({}, region, {
          mapCount: regionMatches ? region.mapCount : matchingMaps.length
        }))
        matchingMaps.forEach(map => {
          items.push({
            type: 'regionMap',
            region: region.name,
            map
          })
        })
      })
      return items
    },
    selectedMapInfo () {
      return this.mapByName[this.activeMap] || null
    },
    currentWorkflowBatches () {
      return (this.workflowBatches || [])
        .filter(batch => batch.status === this.workflowView)
        .slice()
        .sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')))
    },
    activeBatch () {
      if (this.workflowView === 'live') return null
      const byID = (this.currentWorkflowBatches || []).find(batch => Number(batch.id) === Number(this.activeBatchId))
      if (byID) return byID
      return (this.currentWorkflowBatches || [])[0] || null
    },
    canReviewActiveBatch () {
      return !!(this.activeBatch && this.activeBatch.canReview)
    },
    selectedSpawns () {
      if (this.editMode) return this.editRows
      if (this.workflowView !== 'live') return this.activeBatch ? (this.activeBatch.spawns || []) : []
      return this.spawnsByMap[this.activeMap] || []
    },
    visibleSelectedSpawns () {
      return this.selectedSpawns.filter(spawn => this.passesActiveFilters(spawn))
    },
    analysisSpawns () {
      return this.selectedSpawns.filter(spawn => this.membershipActive || !spawn.memberOnly)
    },
    landNatureSpawns () {
      return this.visibleSelectedSpawns.filter(spawn => !this.isWaterSpawn(spawn))
    },
    waterNatureSpawns () {
      return this.visibleSelectedSpawns.filter(spawn => this.isWaterSpawn(spawn) && !this.isFishingOnly(spawn))
    },
    landNatureAnalysisSpawns () {
      return this.analysisSpawns.filter(spawn => !this.isWaterSpawn(spawn))
    },
    waterNatureAnalysisSpawns () {
      return this.analysisSpawns.filter(spawn => this.isWaterSpawn(spawn) && !this.isFishingOnly(spawn))
    },
    landSpawns () {
      return this.visibleSelectedSpawns
        .filter(spawn => !this.isWaterSpawn(spawn))
        .slice()
        .sort(this.compareSpawns)
    },
    waterSpawns () {
      return this.visibleSelectedSpawns
        .filter(spawn => this.isWaterSpawn(spawn))
        .slice()
        .sort(this.compareSpawns)
    },
    showLand () {
      return this.displayMode === 'stacked' || this.displayMode === 'land'
    },
    showWater () {
      return this.displayMode === 'stacked' || this.displayMode === 'water'
    },
    movesByMonsterID () {
      const grouped = Object.create(null)
      ;(this.spawnMoves || []).forEach(move => {
        const monsterID = Number(move.monsterID || 0)
        if (!monsterID || Number(move.level || 0) <= 0) return
        if (!grouped[monsterID]) grouped[monsterID] = []
        grouped[monsterID].push({
          id: Number(move.id || 0),
          monsterID,
          level: Number(move.level || 0),
          skillID: Number(move.skillID || 0),
          name: move.name || ('#' + move.skillID)
        })
      })
      Object.keys(grouped).forEach(monsterID => {
        grouped[monsterID].sort((a, b) => {
          const levelCompare = a.level - b.level
          if (levelCompare !== 0) return levelCompare
          return a.id - b.id
        })
      })
      return grouped
    },
    selfHarmingMoveSet () {
      const moves = this.referenceData && this.referenceData.selfHarmingMoves ? this.referenceData.selfHarmingMoves : []
      return moves.reduce((set, move) => {
        const skillID = Number(move || 0)
        if (skillID > 0) {
          set[skillID] = true
        }
        return set
      }, Object.create(null))
    },
    preferredNaturesByDexID () {
      const source = this.referenceData && this.referenceData.preferredPokemonNatures ? this.referenceData.preferredPokemonNatures : {}
      return Object.keys(source).reduce((result, dexID) => {
        const key = String(Number(dexID || 0))
        if (key === '0') return result
        result[key] = (source[dexID] || []).map(nature => String(nature || '').trim()).filter(nature => nature)
        return result
      }, Object.create(null))
    },
    usageRanksByDexID () {
      const result = Object.create(null)
      ;(this.spawnUsageRanks || []).forEach(rank => {
        if (!rank || Number(rank.rank || 0) < 1 || Number(rank.rank || 0) > 100) return
        const lineDexIDs = Array.isArray(rank.lineDexIDs) && rank.lineDexIDs.length ? rank.lineDexIDs : [rank.dexID]
        lineDexIDs.forEach(dexID => {
          const numericDex = Number(dexID || 0)
          if (!numericDex) return
          if (!result[numericDex]) result[numericDex] = []
          result[numericDex].push(rank)
        })
      })
      Object.keys(result).forEach(dexID => {
        result[dexID].sort((a, b) => Number(a.rank || 0) - Number(b.rank || 0))
      })
      return result
    },
    pokemonCatalogOptions () {
      const source = (this.spawnPokemonCatalog && this.spawnPokemonCatalog.length ? this.spawnPokemonCatalog : Monsters.map(monster => ({
        dexID: Number(monster.ID || 0),
        name: monster.Name || ('#' + monster.ID),
        type1: '',
        type2: '',
        evolutionStage: ''
      })))
      return source
        .map(entry => ({
          dexID: Number(entry.dexID || entry.ID || 0),
          name: entry.name || entry.Name || ('#' + entry.dexID),
          type1: String(entry.type1 || '').trim(),
          type2: String(entry.type2 || '').trim(),
          evolutionStage: String(entry.evolutionStage || '').trim()
        }))
        .filter(entry => entry.dexID > 0 && entry.name)
    },
    pokemonCatalogByDexID () {
      return this.pokemonCatalogOptions.reduce((result, option) => {
        result[String(option.dexID)] = option
        return result
      }, Object.create(null))
    },
    addSpawnSortOptions () {
      return ADD_SPAWN_SORT_OPTIONS
    },
    addSpawnFilterGroups () {
      return [
        { key: 'types', label: 'Type', options: this.addSpawnTypeOptions },
        { key: 'rarities', label: 'Rarity', options: this.addSpawnRarityOptions },
        { key: 'habitats', label: 'Habitat', options: this.addSpawnHabitatOptions },
        { key: 'evolutionStages', label: 'Evolution', options: this.addSpawnEvolutionOptions },
        { key: 'regions', label: 'Region', options: this.addSpawnRegionOptions },
        { key: 'events', label: 'Event Forms', options: this.addSpawnEventOptions },
        { key: 'natures', label: 'Preferred Natures', options: this.addSpawnNatureOptions },
        { key: 'topUsage', label: 'Top 100 PvP Usage', options: [] }
      ]
    },
    addSpawnTypeOptions () {
      const seen = Object.create(null)
      this.pokemonCatalogOptions.forEach(option => {
        ;[option.type1, option.type2].forEach(type => {
          const value = String(type || '').trim()
          if (value) seen[value] = true
        })
      })
      return Object.keys(seen).sort().map(type => ({ value: type, text: type }))
    },
    addSpawnRarityOptions () {
      const tiers = this.referenceData && this.referenceData.pokemonRarityTiers ? this.referenceData.pokemonRarityTiers : {}
      const seen = Object.create(null)
      Object.keys(tiers).forEach(dexID => {
        ;(Array.isArray(tiers[dexID]) ? tiers[dexID] : [tiers[dexID]]).forEach(tier => {
          const value = String(tier || '').trim()
          if (value) seen[value] = true
        })
      })
      return Object.keys(seen)
        .sort((a, b) => this.raritySortValueForTier(a) - this.raritySortValueForTier(b))
        .map(tier => ({ value: tier, text: this.tierDisplayLabel(tier) }))
    },
    itemOptions () {
      const items = this.referenceLookups && this.referenceLookups.items ? this.referenceLookups.items : {}
      return Object.keys(items)
        .map(id => ({
          id: Number(id || 0),
          name: String(items[id] || '').trim()
        }))
        .filter(item => item.id > 0 && item.name)
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
    },
    itemNameByID () {
      return this.itemOptions.reduce((result, item) => {
        result[String(item.id)] = item.name
        return result
      }, Object.create(null))
    },
    itemIDByNormalizedName () {
      return this.itemOptions.reduce((result, item) => {
        const key = this.normalizeLookupText(item.name)
        if (key && !result[key]) result[key] = item.id
        return result
      }, Object.create(null))
    },
    addSpawnHabitatOptions () {
      const habitats = this.referenceData && this.referenceData.habitats ? this.referenceData.habitats : {}
      return Object.keys(habitats).sort().map(habitat => ({ value: habitat, text: habitat }))
    },
    addSpawnNatureOptions () {
      const natures = Object.create(null)
      const source = this.referenceData && this.referenceData.preferredPokemonNatures ? this.referenceData.preferredPokemonNatures : {}
      Object.keys(source).forEach(dexID => {
        ;(source[dexID] || []).forEach(nature => {
          const value = String(nature || '').trim()
          if (value) natures[value] = true
        })
      })
      return Object.keys(natures).sort().map(nature => ({ value: nature, text: nature }))
    },
    addSpawnEventOptions () {
      const names = this.formMapping && this.formMapping.names ? this.formMapping.names : {}
      const seen = Object.create(null)
      Object.keys(names).forEach(key => {
        const name = String(names[key] || '').trim()
        if (this.isShinyFormName(name)) return
        if (name) seen[name] = true
      })
      return Object.keys(seen).sort().map(name => ({ value: name, text: name }))
    },
    addSpawnRegionOptions () {
      return REGION_FILTERS.map(region => ({ value: region.value, text: region.text }))
    },
    addSpawnEvolutionOptions () {
      return EVOLUTION_STAGE_OPTIONS
    },
    filteredAddSpawnPokemon () {
      const search = String(this.addSpawnModal.search || '').trim().toLowerCase()
      const filters = this.addSpawnModal.filters
      const activeFilters = this.addSpawnModal.activeFilters || {}
      const rows = this.pokemonCatalogOptions
        .filter(option => {
          if (search && option.name.toLowerCase().indexOf(search) === -1 && String(option.dexID).indexOf(search) === -1) return false
          if (activeFilters.types && filters.types.length && !filters.types.some(type => this.pokemonOptionTypes(option).indexOf(String(type).toLowerCase()) !== -1)) return false
          if (activeFilters.rarities && filters.rarities.length && !filters.rarities.some(tier => this.referenceTiersForPokemon(option.dexID).indexOf(tier) !== -1)) return false
          if (activeFilters.habitats && filters.habitats.length && !filters.habitats.some(habitat => this.pokemonInHabitat(option.dexID, habitat))) return false
          if (activeFilters.evolutionStages && filters.evolutionStages.length && filters.evolutionStages.indexOf(option.evolutionStage) === -1) return false
          if (activeFilters.regions && filters.regions.length && !filters.regions.some(region => this.pokemonInRegion(option.dexID, region))) return false
          if (activeFilters.events && filters.events.length && !filters.events.some(event => this.pokemonHasEventCategory(option.dexID, event))) return false
          if (activeFilters.natures && filters.natures.length && !filters.natures.some(nature => this.naturesForDexID(option.dexID).indexOf(nature) !== -1)) return false
          if (activeFilters.topUsage && !this.usageRanksByDexID[Number(option.dexID || 0)]) return false
          return true
        })
      return rows.sort((a, b) => {
        const direction = this.addSpawnModal.sortDirection === 'desc' ? -1 : 1
        if (this.addSpawnModal.sortKey === 'name') {
          return direction * a.name.localeCompare(b.name, undefined, { numeric: true })
        }
        if (this.addSpawnModal.sortKey === 'rarity') {
          const rarityCompare = this.addSpawnRaritySortValue(a) - this.addSpawnRaritySortValue(b)
          if (rarityCompare !== 0) return direction * rarityCompare
          return direction * a.name.localeCompare(b.name, undefined, { numeric: true })
        }
        return direction * (Number(a.dexID || 0) - Number(b.dexID || 0))
      })
    },
    availableAddSpawnPokemon () {
      return this.filteredAddSpawnPokemon.filter(pokemon => !this.addSpawnModal.selected[String(pokemon.dexID)])
    },
    selectedAddSpawnPokemon () {
      const selected = this.addSpawnModal.selected || {}
      const ordered = (this.addSpawnModal.selectedOrder || []).filter(dexID => selected[String(dexID)])
      Object.keys(selected).forEach(dexID => {
        if (selected[dexID] && ordered.indexOf(dexID) === -1) ordered.push(dexID)
      })
      return ordered
        .map(dexID => this.pokemonCatalogByDexID[String(dexID)])
        .filter(pokemon => pokemon)
    },
    selectedAddSpawnCount () {
      return this.selectedAddSpawnPokemon.length
    },
    canUseAddSpawnLevelRange () {
      const min = Number(this.draftLevelMin || 0)
      const max = Number(this.draftLevelMax || 0)
      return min >= 1 && max >= min && max <= 100
    },
    canAddSpawnInEdit () {
      if (!this.editMode) return false
      return this.canUseAddSpawnLevelRange
    },
    landNatureStats () {
      return this.natureStatsForSpawns(this.landNatureSpawns)
    },
    waterNatureStats () {
      return this.natureStatsForSpawns(this.waterNatureSpawns)
    },
    selfHarmingWarnings () {
      if (!this.showWarnings) return []
      const seen = Object.create(null)
      const warnings = []
      this.visibleSelectedSpawns.forEach(spawn => {
        this.possibleMoves(spawn).forEach(move => {
          if (!this.isSelfHarmingMove(move, spawn)) return
          const levelRange = move.rangeText || (spawn.minLVL + '-' + spawn.maxLVL)
          const key = this.pokemonName(spawn) + '-' + move.name + '-' + levelRange
          if (seen[key]) return
          seen[key] = true
          warnings.push({
            key,
            text: this.pokemonName(spawn) + ' can know ' + move.name + ' at Lv. ' + levelRange + '.'
          })
        })
      })
      return warnings.sort((a, b) => a.text.localeCompare(b.text, undefined, { numeric: true }))
    },
    selfHarmingWarningBySpawnKey () {
      if (!this.showWarnings) return Object.create(null)
      const result = Object.create(null)
      this.visibleSelectedSpawns.forEach(spawn => {
        if (this.possibleMoves(spawn).some(move => this.isSelfHarmingMove(move, spawn))) {
          result[this.spawnKey(spawn)] = true
        }
      })
      return result
    },
    repelWarnings () {
      if (!this.showWarnings) return []
      const seen = Object.create(null)
      const entries = Object.values(this.repelInfoBySpawnKey).filter(entry => {
        const key = entry.dexID + '-' + entry.minLevel + '-' + entry.reason + '-' + entry.periods.map(period => period.key).join('-')
        if (seen[key]) return false
        seen[key] = true
        return true
      })
      return entries
        .map(entry => ({
          key: 'repel-' + entry.dexID + '-' + entry.minLevel + '-' + entry.reason,
          text: entry.name + ' can ' + (entry.reason === 'solo' ? 'be isolated' : 'leave only T7+ Pokemon') + ' with Repel from Lv. ' + entry.minLevel + ' during ' + this.formatPeriodLabels(entry.periods) + '.'
        }))
        .sort((a, b) => a.text.localeCompare(b.text, undefined, { numeric: true }))
    },
    repelInfoBySpawnKey () {
      if (!this.showWarnings) return Object.create(null)
      return this.buildRepelInfoBySpawnKey(this.selectedSpawns)
    },
    landNatureConflictWarnings () {
      if (!this.showWarnings) return []
      return this.buildNatureConflictWarnings(this.landNatureAnalysisSpawns, 'land')
    },
    waterNatureConflictWarnings () {
      if (!this.showWarnings) return []
      return this.buildNatureConflictWarnings(this.waterNatureAnalysisSpawns, 'water')
    },
    natureConflictWarnings () {
      return this.landNatureConflictWarnings.concat(this.waterNatureConflictWarnings)
    },
    natureConflictBySpawnKey () {
      if (!this.showWarnings) return Object.create(null)
      const spawnKeys = Object.create(null)
      this.natureConflictWarnings.forEach(warning => {
        warning.spawnKeys.forEach(key => {
          spawnKeys[key] = true
        })
      })
      const result = Object.create(null)
      this.selectedSpawns.forEach(spawn => {
        if (spawnKeys[this.spawnKey(spawn)]) {
          result[this.spawnKey(spawn)] = true
        }
      })
      return result
    }
  },
  beforeRouteEnter (to, from, next) {
    next(vm => {
      vm.resetMapSelection()
    })
  },
  watch: {
    workflowView () {
      this.discardEdit()
      this.activeBatchId = null
      this.activeMap = ''
      this.mapSearch = ''
      this.selectedSearchEntity = null
      this.$nextTick(() => {
        if (this.maps.length) {
          this.activeMap = this.maps[0].name
          this.activeBatchId = this.maps[0].batchID || null
        }
      })
    },
    mapSearchMode () {
      this.mapSearch = ''
      this.selectedSearchEntity = null
    },
    maps: {
      immediate: true,
      handler (maps) {
        if (!maps.length) {
          this.activeMap = ''
          return
        }
        if (!this.activeMap || !maps.some(map => map.name === this.activeMap)) {
          this.activeMap = maps[0].name
          this.activeBatchId = maps[0].batchID || null
        }
      }
    },
    spawnsUpdatedAt () {
      this.loading = false
      if (this.loadingTimer) {
        window.clearTimeout(this.loadingTimer)
        this.loadingTimer = null
      }
      this.clearRepelCache()
    },
    workflowUpdatedAt () {
      if (this.workflowView !== 'live' && this.activeBatchId && !this.currentWorkflowBatches.some(batch => Number(batch.id) === Number(this.activeBatchId))) {
        this.activeBatchId = null
      }
      if (!this.isWorkflowViewVisible(this.workflowView)) {
        this.workflowView = 'wip'
      }
    },
    spawnMoves () {
      this.moveCache = Object.create(null)
    },
    sortKey () {
      this.savePreferences()
    },
    sortDirection () {
      this.savePreferences()
    },
    membershipActive () {
      this.clearRepelCache()
      this.savePreferences()
    },
    showWarnings () {
      this.clearRepelCache()
      this.savePreferences()
    }
  },
  created () {
    this.loadPreferences()
    this.refreshSpawns()
    this.emit('getSpawnEditorWorkflow', {})
    this.emit('getSpawnReferenceData', {})
    this.emit('getFormMapping', '')
  },
  beforeDestroy () {
    if (this.loadingTimer) {
      window.clearTimeout(this.loadingTimer)
    }
  },
  methods: {
    refreshSpawns () {
      this.loading = true
      this.emit('getSpawns', {})
      this.emit('getSpawnEditorWorkflow', {})
      if (this.loadingTimer) {
        window.clearTimeout(this.loadingTimer)
      }
      this.loadingTimer = window.setTimeout(() => {
        this.loading = false
        this.loadingTimer = null
      }, 15000)
    },
    setWorkflowView (view) {
      if (view === this.workflowView) return
      if (!this.isWorkflowViewVisible(view)) return
      if (this.editMode && !this.confirmDiscardEdit()) return
      this.discardEdit()
      this.workflowView = view
    },
    isWorkflowViewVisible (view) {
      return this.visibleWorkflowViewOptions.some(option => option.value === view)
    },
    workflowStatusLabel (status) {
      const match = WORKFLOW_VIEW_OPTIONS.find(option => option.value === status)
      return match ? match.text : String(status || '')
    },
    reviewDecisionLabel (review) {
      const name = String(review && review.reviewerName ? review.reviewerName : 'Unknown')
      const decision = String(review && review.decision ? review.decision : '').toLowerCase()
      if (decision === 'approved') return 'Approved by ' + name
      if (decision === 'rejected') return 'Rejected by ' + name
      return name
    },
    showSpawnSectionActions (area) {
      if (!this.editMode && !this.canBeginEdit) return false
      if (area === 'land') return this.showLand
      return !this.showLand && this.showWater
    },
    beginEdit () {
      if (!this.activeMap && this.workflowView === 'live') return
      const rows = (this.workflowView === 'live' ? this.selectedSpawns : (this.activeBatch ? this.activeBatch.spawns : [])) || []
      this.editRows = rows.map(row => this.cloneSpawn(row))
      if (rows.length) {
        const defaults = this.defaultLevelRangeForRows(rows)
        this.draftLevelMin = defaults.min
        this.draftLevelMax = defaults.max
      } else {
        this.draftLevelMin = null
        this.draftLevelMax = null
      }
      this.editMode = true
    },
    cancelEdit () {
      if (this.editMode && !this.confirmDiscardEdit()) return
      this.discardEdit()
    },
    confirmDiscardEdit () {
      return window.confirm('All unsaved spawn changes will be lost. Continue?')
    },
    discardEdit () {
      this.editMode = false
      this.editRows = []
      this.addSpawnModal.visible = false
    },
    cloneSpawn (spawn) {
      return JSON.parse(JSON.stringify(spawn || {}))
    },
    saveDraft () {
      if (!this.editMode) return
      const mapName = this.activeMap || (this.activeBatch && this.activeBatch.mapName) || ''
      if (!mapName) return
      const missingTimeSpawns = this.editRows.filter(row => !this.activeTimeKeys(row).length)
      if (missingTimeSpawns.length) {
        const names = missingTimeSpawns
          .map(row => this.pokemonName(row))
          .filter((name, index, list) => list.indexOf(name) === index)
        window.alert('Cannot save. The following Pokemon have no time selected: ' + names.join(', ') + '.')
        return
      }
      this.emit('saveSpawnDraft', {
        id: this.workflowView === 'wip' && this.activeBatch ? this.activeBatch.id : 0,
        mapName,
        spawns: this.editRows.map(row => this.prepareSpawnForSave(row, mapName))
      })
      this.editMode = false
    },
    prepareSpawnForSave (spawn, mapName) {
      const row = this.cloneSpawn(spawn)
      row.map = mapName
      row.area = this.isWaterSpawn(row) ? 'surf' : 'land'
      row.minLVL = this.clampNumber(row.minLVL, 1, 100)
      row.maxLVL = Math.max(this.clampNumber(row.maxLVL, 1, 100), row.minLVL)
      row.rarity = Math.max(Number(row.rarity || 0), 0)
      this.normalizeSpawnItemForSave(row)
      row.memberOnly = !!row.memberOnly
      row.start = this.clampNumber(row.start, 0, 23)
      row.end = this.clampNumber(row.end, 0, 23)
      row.steps = Number(row.steps || 15) || 15
      row.forms = row.forms || '0'
      row.formRarity = Number(row.formRarity || 0)
      delete row.noTimeSelected
      if (!this.isWaterSpawn(row)) {
        row.required_rod = 0
      } else {
        row.required_rod = this.clampNumber(row.required_rod, 0, 3)
      }
      const fishOnly = row.fishOnly === undefined ? this.isFishingOnly(row) : row.fishOnly
      if (this.isWaterSpawn(row) && fishOnly) {
        row.x1 = 0
        row.x2 = 0
        row.y1 = 0
        row.y2 = 0
      } else {
        row.x1 = 0
        row.x2 = 300
        row.y1 = 0
        row.y2 = 300
      }
      delete row.fishOnly
      return row
    },
    clampNumber (value, min, max) {
      const number = Number(value || 0)
      if (!Number.isFinite(number)) return min
      return Math.min(Math.max(Math.round(number), min), max)
    },
    setDraftMinLevel (value) {
      const max = this.clampNumber(this.draftLevelMax || 100, 1, 100)
      const min = Math.min(this.clampNumber(value, 1, 100), max)
      this.draftLevelMin = min
      this.draftLevelMax = Math.max(max, min)
    },
    setDraftMaxLevel (value) {
      const min = this.clampNumber(this.draftLevelMin || 1, 1, 100)
      this.draftLevelMin = min
      this.draftLevelMax = Math.max(this.clampNumber(value, 1, 100), min)
    },
    setSpawnMinLevel (spawn, value) {
      const currentMax = this.clampNumber(spawn.maxLVL || 100, 1, 100)
      const min = Math.min(this.clampNumber(value, 1, 100), currentMax)
      this.$set(spawn, 'minLVL', min)
      if (Number(spawn.maxLVL || 0) < min) {
        this.$set(spawn, 'maxLVL', min)
      }
      this.moveCache = Object.create(null)
    },
    setSpawnMaxLevel (spawn, value) {
      const min = this.clampNumber(spawn.minLVL || 1, 1, 100)
      const max = Math.max(this.clampNumber(value, 1, 100), min)
      this.$set(spawn, 'minLVL', min)
      this.$set(spawn, 'maxLVL', max)
      this.moveCache = Object.create(null)
    },
    setSpawnRarity (spawn, value) {
      const rarity = Number(value || 0)
      this.$set(spawn, 'rarity', Number.isFinite(rarity) ? Math.max(rarity, 0) : 0)
    },
    normalizeLookupText (value) {
      return String(value || '').trim().toLowerCase()
    },
    itemOptionForName (name) {
      const key = this.normalizeLookupText(name)
      const id = this.itemIDByNormalizedName[key]
      if (!id) return null
      return {
        id,
        name: this.itemNameByID[String(id)] || String(name || '').trim()
      }
    },
    itemNameInputValue (spawn) {
      const text = String(spawn.item || '').trim()
      if (text) return text
      const id = Number(spawn.itemID || 0)
      return id > 0 ? this.itemNameByID[String(id)] || '' : ''
    },
    itemInputState (spawn) {
      const text = this.itemNameInputValue(spawn)
      if (!text) return null
      return !!this.itemOptionForName(text)
    },
    hasPredefinedItemDropChance (itemID) {
      const chances = this.referenceData && this.referenceData.itemDropChances ? this.referenceData.itemDropChances : {}
      return Object.prototype.hasOwnProperty.call(chances, String(Number(itemID || 0)))
    },
    predefinedItemDropChance (itemID) {
      const chances = this.referenceData && this.referenceData.itemDropChances ? this.referenceData.itemDropChances : {}
      return Number(chances[String(Number(itemID || 0))] || 0) / 100
    },
    setSpawnItemName (spawn, value) {
      const text = String(value || '').trim()
      const previousID = Number(spawn.itemID || 0)
      this.$set(spawn, 'item', text || null)
      if (!text) {
        this.$set(spawn, 'itemID', 0)
        this.$set(spawn, 'itemRarity', 0)
        return
      }

      const option = this.itemOptionForName(text)
      if (!option) {
        this.$set(spawn, 'itemID', 0)
        this.$set(spawn, 'itemRarity', 0)
        return
      }

      this.$set(spawn, 'itemID', option.id)
      this.$set(spawn, 'item', option.name)
      if (option.id !== previousID) {
        this.$set(spawn, 'itemRarity', this.hasPredefinedItemDropChance(option.id) ? this.predefinedItemDropChance(option.id) : 0)
      }
    },
    setSpawnItemDropChance (spawn, value) {
      const chance = Number(value || 0)
      this.$set(spawn, 'itemRarity', Number.isFinite(chance) ? Math.max(chance, 0) / 100 : 0)
    },
    normalizeSpawnItemForSave (row) {
      const option = this.itemOptionForName(row.item)
      if (option) {
        row.itemID = option.id
        row.item = option.name
      } else {
        row.itemID = this.clampNumber(row.itemID, 0, 999999)
        if (row.itemID > 0 && this.itemNameByID[String(row.itemID)]) {
          row.item = this.itemNameByID[String(row.itemID)]
        }
      }
      row.itemRarity = row.itemID > 0 ? Math.max(Number(row.itemRarity || 0), 0) : 0
      if (row.itemID <= 0) {
        row.itemID = 0
        row.item = null
      }
    },
    defaultLevelRangeForMap () {
      const draftMin = Number(this.draftLevelMin || 0)
      const draftMax = Number(this.draftLevelMax || 0)
      if (draftMin >= 1 && draftMax >= draftMin) return { min: draftMin, max: draftMax }
      const rows = this.editRows.length ? this.editRows : (this.workflowView === 'live' ? (this.liveSpawnsByMap[this.activeMap] || []) : (this.activeBatch ? this.activeBatch.spawns || [] : []))
      if (rows.length) {
        return this.defaultLevelRangeForRows(rows)
      }
      return { min: 0, max: 0 }
    },
    defaultLevelRangeForRows (rows) {
      return {
        min: rows.reduce((min, spawn) => Math.min(min, Number(spawn.minLVL || 1)), 100),
        max: rows.reduce((max, spawn) => Math.max(max, Number(spawn.maxLVL || 1)), 1)
      }
    },
    openNewMapModal () {
      if (this.workflowView !== 'wip') return
      this.newMapModal = {
        visible: true,
        mapName: ''
      }
    },
    createNewMapDraft () {
      const mapName = String(this.newMapModal.mapName || '').trim()
      if (!mapName) return
      this.workflowView = 'wip'
      this.activeMap = mapName
      this.activeBatchId = null
      this.draftLevelMin = null
      this.draftLevelMax = null
      this.editRows = []
      this.editMode = true
      this.newMapModal.visible = false
    },
    openAddSpawnModal (area) {
      if (!this.editMode) return
      this.syncDraftLevelRangeForAddSpawn()
      if (!this.canAddSpawnInEdit) return
      this.addSpawnModal.area = area || (this.displayMode === 'water' ? 'surf' : 'land')
      this.addSpawnModal.search = ''
      this.addSpawnModal.selected = {}
      this.addSpawnModal.selectedOrder = []
      this.addSpawnModal.draggingDexID = null
      this.addSpawnModal.visible = true
    },
    syncDraftLevelRangeForAddSpawn () {
      if (!this.editRows.length) return
      const defaults = this.defaultLevelRangeForRows(this.editRows)
      this.draftLevelMin = defaults.min
      this.draftLevelMax = defaults.max
    },
    addAddSpawnSelection (dexID) {
      const numericDexID = Number(dexID || 0)
      if (!numericDexID || Number.isNaN(numericDexID)) return
      const key = String(numericDexID)
      if (this.addSpawnModal.selected[key]) return
      this.$set(this.addSpawnModal.selected, key, true)
      this.addSpawnModal.selectedOrder.push(key)
    },
    removeAddSpawnSelection (dexID) {
      const numericDexID = Number(dexID || 0)
      if (!numericDexID || Number.isNaN(numericDexID)) return
      const key = String(numericDexID)
      this.$delete(this.addSpawnModal.selected, key)
      this.addSpawnModal.selectedOrder = this.addSpawnModal.selectedOrder.filter(entry => String(entry) !== key)
    },
    startAddSpawnDrag (pokemon, event) {
      const key = String(pokemon.dexID)
      this.addSpawnModal.draggingDexID = key
      if (event && event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move'
        event.dataTransfer.setData('text/plain', key)
      }
    },
    endAddSpawnDrag () {
      this.addSpawnModal.draggingDexID = null
    },
    dropAddSpawnPokemon (event) {
      const key = event && event.dataTransfer ? event.dataTransfer.getData('text/plain') : this.addSpawnModal.draggingDexID
      this.addAddSpawnSelection(key || this.addSpawnModal.draggingDexID)
      this.addSpawnModal.draggingDexID = null
    },
    addSelectedSpawns () {
      if (!this.canUseAddSpawnLevelRange) return
      const selected = this.selectedAddSpawnPokemon
      selected.forEach(pokemon => {
        this.editRows.push(this.createSpawnForPokemon(pokemon, this.addSpawnModal.area))
      })
      this.addSpawnModal.visible = false
      this.addSpawnModal.selected = {}
      this.addSpawnModal.selectedOrder = []
      this.addSpawnModal.draggingDexID = null
    },
    createSpawnForPokemon (pokemon, area) {
      const range = this.defaultLevelRangeForMap()
      const water = area === 'water' || area === 'surf'
      const itemID = Number((this.referenceData.itemDrops || {})[String(pokemon.dexID)] || 0)
      const chance = itemID > 0 ? Number((this.referenceData.itemDropChances || {})[String(itemID)] || 0) / 100 : 0
      const tier = this.referenceTiersForPokemon(pokemon.dexID)[0] || 'T1'
      return {
        id: -Date.now() - this.editRows.length,
        map: this.activeMap,
        pokemon: pokemon.name,
        dexID: pokemon.dexID,
        type1: pokemon.type1 || '',
        type2: pokemon.type2 || '',
        minLVL: range.min,
        maxLVL: range.max,
        itemID: itemID,
        item: itemID > 0 ? this.itemNameByID[String(itemID)] || null : null,
        itemRarity: chance,
        memberOnly: false,
        rarity: this.rarityValueForTier(tier, water),
        area: water ? 'surf' : 'land',
        x1: 0,
        x2: 300,
        y1: 0,
        y2: 300,
        steps: 15,
        required_rod: 0,
        start: 0,
        end: 0,
        forms: '0',
        formRarity: 0,
        approved: false,
        fishOnly: false
      }
    },
    deleteEditSpawn (spawn) {
      const key = this.spawnKey(spawn)
      this.editRows = this.editRows.filter(row => this.spawnKey(row) !== key)
    },
    deleteAllEditSpawns () {
      this.editRows = []
    },
    setSpawnFishOnly (spawn, value) {
      this.$set(spawn, 'fishOnly', !!value)
      if (value) {
        spawn.x1 = 0
        spawn.x2 = 0
        spawn.y1 = 0
        spawn.y2 = 0
        if (Number(spawn.required_rod || 0) <= 0) spawn.required_rod = 1
      } else {
        spawn.x1 = 0
        spawn.x2 = 300
        spawn.y1 = 0
        spawn.y2 = 300
      }
    },
    updateSpawnRarityFromTier (spawn, tier) {
      spawn.rarity = this.rarityValueForTier(tier, this.isWaterSpawn(spawn))
    },
    rarityValueForTier (tier, water) {
      const table = water ? WATER_RARITIES : LAND_RARITIES
      const match = table.find(entry => entry.tier === tier)
      return match ? match.value : Number(tier || 0) || 0
    },
    moveActiveBatch (status) {
      if (!this.activeBatch) return
      this.emit('moveSpawnBatchStatus', { id: this.activeBatch.id, status })
    },
    removeActiveBatch () {
      if (!this.activeBatch) return
      this.emit('removeSpawnBatch', { id: this.activeBatch.id })
    },
    reviewActiveBatch (decision) {
      if (!this.canReviewActiveBatch) return
      this.emit('reviewSpawnBatch', { id: this.activeBatch.id, decision, comment: '' })
    },
    uploadActiveBatch () {
      if (!this.activeBatch) return
      this.emit('uploadReadySpawnBatch', { id: this.activeBatch.id })
    },
    rejectReadyActiveBatch () {
      if (!this.activeBatch) return
      this.emit('rejectReadySpawnBatch', { id: this.activeBatch.id })
    },
    comparisonRowsForActiveBatch () {
      if (!this.activeBatch) return []
      const before = this.liveSpawnsByMap[this.activeBatch.mapName] || []
      const after = this.activeBatch.spawns || []
      return this.buildComparisonRows(before, after)
    },
    buildComparisonRows (before, after) {
      const beforeMap = this.comparisonMap(before)
      const afterMap = this.comparisonMap(after)
      const rows = []
      Object.keys(afterMap).forEach(key => {
        if (!beforeMap[key]) {
          rows.push({ key: 'add-' + key, type: 'Added', area: this.areaLabel(afterMap[key]), pokemon: this.pokemonName(afterMap[key]), before: '-', after: this.compactSpawnSummary(afterMap[key]) })
          return
        }
        const beforeSummary = this.compactSpawnSummary(beforeMap[key])
        const afterSummary = this.compactSpawnSummary(afterMap[key])
        if (beforeSummary !== afterSummary) {
          rows.push({ key: 'edit-' + key, type: 'Edited', area: this.areaLabel(afterMap[key]), pokemon: this.pokemonName(afterMap[key]), before: beforeSummary, after: this.changedSpawnSummary(beforeMap[key], afterMap[key]) })
        }
      })
      Object.keys(beforeMap).forEach(key => {
        if (!afterMap[key]) {
          rows.push({ key: 'delete-' + key, type: 'Deleted', area: this.areaLabel(beforeMap[key]), pokemon: this.pokemonName(beforeMap[key]), before: this.compactSpawnSummary(beforeMap[key]), after: '-' })
        }
      })
      return rows.sort((a, b) => {
        if (a.area !== b.area) return a.area === 'Land' ? -1 : 1
        return a.pokemon.localeCompare(b.pokemon, undefined, { numeric: true })
      })
    },
    comparisonMap (spawns) {
      const map = Object.create(null)
      ;(spawns || []).forEach((spawn, index) => {
        const key = Number(spawn.id || 0) > 0 ? this.areaLabel(spawn) + '-' + spawn.id : this.areaLabel(spawn) + '-' + spawn.dexID + '-' + index
        map[key] = spawn
      })
      return map
    },
    compactSpawnSummary (spawn) {
      return this.spawnSummaryParts(spawn)
        .filter(part => part.display)
        .map(part => part.value)
        .join(' | ')
    },
    changedSpawnSummary (before, after) {
      const beforeMap = this.spawnSummaryPartMap(before)
      const afterMap = this.spawnSummaryPartMap(after)
      const order = []
      this.spawnSummaryParts(after).concat(this.spawnSummaryParts(before)).forEach(part => {
        if (order.indexOf(part.key) === -1) order.push(part.key)
      })
      const changes = order.reduce((result, key) => {
        const beforePart = beforeMap[key]
        const afterPart = afterMap[key]
        if (!beforePart && !afterPart) return result
        const fallbackLabel = (afterPart || beforePart).label
        const beforeValue = beforePart ? beforePart.value : fallbackLabel + ' -'
        const afterValue = afterPart ? afterPart.value : fallbackLabel + ' -'
        if (beforeValue !== afterValue) result.push(afterValue)
        return result
      }, [])
      return changes.length ? changes.join(' | ') : this.compactSpawnSummary(after)
    },
    spawnSummaryPartMap (spawn) {
      return this.spawnSummaryParts(spawn).reduce((result, part) => {
        result[part.key] = part
        return result
      }, Object.create(null))
    },
    spawnSummaryParts (spawn) {
      const timeKeys = this.activeTimeKeys(spawn)
      const timeLabel = timeKeys.length === TIME_PERIODS.length ? 'All' : (timeKeys.length ? timeKeys.map(key => key.charAt(0).toUpperCase()).join('/') : 'Time None')
      const itemActive = Number(spawn.itemID || 0) > 0
      const water = this.isWaterSpawn(spawn)
      const fishOnly = water && this.isFishingOnly(spawn)
      const parts = [
        { key: 'level', label: 'Lv', value: 'Lv ' + Number(spawn.minLVL || 0) + '-' + Number(spawn.maxLVL || 0), display: true },
        { key: 'rarity', label: 'Rarity', value: this.rarityTier(spawn) + ' ' + this.rawRarity(spawn), display: true },
        { key: 'time', label: 'Time', value: timeLabel, display: true },
        { key: 'item', label: 'Item', value: itemActive ? this.itemName(spawn) + ' ' + this.itemDropLabel(spawn) : 'Item -', display: itemActive },
        { key: 'member', label: 'Membership', value: spawn.memberOnly ? 'Membership: Active' : 'Membership: Inactive', display: !!spawn.memberOnly }
      ]
      if (water) {
        parts.push({ key: 'rod', label: 'Rod', value: this.rodLabel(spawn), display: true })
        parts.push({ key: 'encounter', label: 'Encounter', value: fishOnly ? 'Fish-only' : 'Surf', display: fishOnly })
      }
      return parts
    },
    areaLabel (spawn) {
      return this.isWaterSpawn(spawn) ? 'Water' : 'Land'
    },
    pokemonOptionTypes (option) {
      return [option.type1, option.type2].map(type => String(type || '').trim().toLowerCase()).filter(type => type)
    },
    referenceTiersForPokemon (dexID) {
      const tiers = this.referenceData && this.referenceData.pokemonRarityTiers ? this.referenceData.pokemonRarityTiers : {}
      const value = tiers[String(Number(dexID || 0))]
      if (!value) return []
      return (Array.isArray(value) ? value : [value]).map(tier => String(tier || '').trim()).filter(tier => tier)
    },
    pokemonInHabitat (dexID, habitat) {
      const habitats = this.referenceData && this.referenceData.habitats ? this.referenceData.habitats : {}
      return (habitats[habitat] || []).map(value => Number(value || 0)).indexOf(Number(dexID || 0)) !== -1
    },
    pokemonInRegion (dexID, regionKey) {
      const region = REGION_FILTERS.find(candidate => candidate.value === regionKey)
      const numericDexID = Number(dexID || 0)
      return !!region && region.ranges.some(range => numericDexID >= range[0] && numericDexID <= range[1])
    },
    pokemonHasEventCategory (dexID, eventName) {
      const mappings = this.formMapping && this.formMapping.mappings ? this.formMapping.mappings : {}
      return (mappings[Number(dexID || 0)] || []).some(form => {
        const name = String(form.name || '').trim()
        return name === eventName && !this.isShinyFormName(name)
      })
    },
    naturesForDexID (dexID) {
      return this.preferredNaturesByDexID[String(Number(dexID || 0))] || []
    },
    isShinyFormName (name) {
      return String(name || '').toLowerCase().indexOf('shiny') !== -1
    },
    isAddSpawnFilterActive (key) {
      return !!(this.addSpawnModal.activeFilters || {})[key]
    },
    setAddSpawnFilterActive (key, active) {
      this.$set(this.addSpawnModal.activeFilters, key, !!active)
    },
    addSpawnFilterSelectedCount (key) {
      if (key === 'topUsage') return this.isAddSpawnFilterActive(key) ? 'Active' : 'Off'
      const values = this.addSpawnModal.filters[key] || []
      if (!values.length) return 'All'
      return values.length + ' selected'
    },
    isAddSpawnFilterValueSelected (key, value) {
      return (this.addSpawnModal.filters[key] || []).indexOf(value) !== -1
    },
    toggleAddSpawnFilterValue (key, value, selected) {
      const values = (this.addSpawnModal.filters[key] || []).slice()
      const index = values.indexOf(value)
      if (selected && index === -1) values.push(value)
      if (!selected && index !== -1) values.splice(index, 1)
      this.$set(this.addSpawnModal.filters, key, values)
    },
    resetAddSpawnFilters () {
      this.addSpawnModal.activeFilters = {
        types: true,
        rarities: true,
        habitats: true,
        evolutionStages: true,
        regions: true,
        events: true,
        natures: true,
        topUsage: false
      }
      this.addSpawnModal.filters = {
        types: [],
        rarities: [],
        habitats: [],
        evolutionStages: [],
        regions: [],
        events: [],
        natures: []
      }
      this.addSpawnModal.search = ''
    },
    clearRepelCache () {
      this.repelMapCache = Object.create(null)
    },
    markIconError (key) {
      this.$set(this.iconErrors, key, true)
    },
    warningGroupExpanded (key) {
      return !!this.expandedWarningGroups[key]
    },
    toggleWarningGroup (key) {
      this.$set(this.expandedWarningGroups, key, !this.warningGroupExpanded(key))
    },
    hiddenWarningCount (warnings, key, limit) {
      if (this.warningGroupExpanded(key)) return 0
      return Math.max((warnings || []).length - limit, 0)
    },
    warningRows (warnings, key, limit) {
      if (this.warningGroupExpanded(key)) return warnings || []
      return (warnings || []).slice(0, limit)
    },
    formatPeriodLabels (periods) {
      return (periods || []).map(period => period.label).join('/')
    },
    selectMap (mapName) {
      this.activeMap = mapName
    },
    resetMapSelection () {
      this.activeMap = ''
      this.activeNatureHighlight = ''
      this.activeNatureHighlightArea = ''
      this.mapSearch = ''
      this.mapSearchMode = 'map'
      this.selectedSearchEntity = null
      if (this.maps.length) {
        this.activeMap = this.maps[0].name
      }
    },
    isNatureHighlightActive (nature, area) {
      return this.activeNatureHighlight === nature && this.activeNatureHighlightArea === area
    },
    toggleNatureHighlight (nature, area) {
      if (this.isNatureHighlightActive(nature, area)) {
        this.activeNatureHighlight = ''
        this.activeNatureHighlightArea = ''
        return
      }
      this.activeNatureHighlight = nature
      this.activeNatureHighlightArea = area
    },
    natureStatsForSpawns (spawns) {
      const rows = (spawns || []).filter(spawn => this.naturesForSpawn(spawn).length)
      const denominator = rows.length
      if (denominator < 3) return []
      const counts = Object.create(null)
      rows.forEach(spawn => {
        this.naturesForSpawn(spawn).forEach(nature => {
          counts[nature] = (counts[nature] || 0) + 1
        })
      })
      return Object.keys(counts)
        .filter(nature => counts[nature] >= 3)
        .map(nature => ({
          name: nature,
          count: counts[nature],
          percent: counts[nature] / denominator * 100
        }))
        .sort((a, b) => {
          if (b.percent !== a.percent) return b.percent - a.percent
          return a.name.localeCompare(b.name)
        })
    },
    formatNaturePercent (value) {
      const numeric = Number(value || 0)
      if (!Number.isFinite(numeric)) return '0%'
      return numeric.toFixed(1) + '%'
    },
    spawnCardClasses (spawn, baseClass) {
      return {
        [baseClass]: true,
        'spawn-card-warning': this.spawnHasWarning(spawn),
        'spawn-card-nature-highlight': this.isSpawnNatureHighlighted(spawn)
      }
    },
    isSpawnNatureHighlighted (spawn) {
      if (!this.activeNatureHighlight || !this.activeNatureHighlightArea) return false
      if (!this.spawnSupportsNature(spawn, this.activeNatureHighlight)) return false
      if (this.activeNatureHighlightArea === 'land') return !this.isWaterSpawn(spawn)
      if (this.activeNatureHighlightArea === 'water') return this.isWaterSpawn(spawn) && !this.isFishingOnly(spawn)
      return false
    },
    spawnHasWarning (spawn) {
      const key = this.spawnKey(spawn)
      return !!(this.selfHarmingWarningBySpawnKey[key] || this.repelInfoBySpawnKey[key] || this.natureConflictBySpawnKey[key])
    },
    pvpUsageRanksForSpawn (spawn) {
      return this.usageRanksByDexID[Number(spawn.dexID || 0)] || []
    },
    pvpUsageTooltip (rank) {
      const name = rank && rank.name ? rank.name : 'This Pokemon'
      return name + ' is ' + this.ordinal(rank.rank) + ' Place in Normal Ranked PvP Usage Stats from the last 30 days.'
    },
    ordinal (value) {
      const number = Number(value || 0)
      if (!number) return '-'
      const mod100 = number % 100
      if (mod100 >= 11 && mod100 <= 13) return number + 'th'
      const mod10 = number % 10
      if (mod10 === 1) return number + 'st'
      if (mod10 === 2) return number + 'nd'
      if (mod10 === 3) return number + 'rd'
      return number + 'th'
    },
    selectSidebarItem (item) {
      if (item.type === 'pokemonOption') {
        this.selectedSearchEntity = {
          type: 'pokemon',
          dexID: item.dexID,
          name: item.name
        }
        this.mapSearch = ''
        return
      }
      if (item.type === 'regionOption') {
        this.selectedSearchEntity = {
          type: 'region',
          name: item.name
        }
        this.mapSearch = ''
        return
      }
      if (item.map) {
        this.activeBatchId = item.map.batchID || null
        this.selectMap(item.map.name)
      }
    },
    clearSearchEntity () {
      this.selectedSearchEntity = null
      this.mapSearch = ''
    },
    sidebarItemKey (item) {
      if (item.type === 'pokemonOption') return 'pokemon-' + item.dexID
      if (item.type === 'regionOption') return 'region-' + item.name
      if (item.type === 'pokemonMap') return 'pokemon-map-' + item.dexID + '-' + item.map.name
      if (item.type === 'regionMap') return 'region-map-' + item.region + '-' + item.map.name
      return 'map-' + (item.map.batchID || item.map.name)
    },
    exactPokemonSearchMatch () {
      const search = String(this.mapSearch || '').trim().toLowerCase()
      if (!search) return null
      return this.pokemonOptionsByName[search] || null
    },
    exactRegionSearchMatch () {
      const search = String(this.mapSearch || '').trim().toLowerCase()
      if (!search) return null
      return this.regionOptionsByName[search] || null
    },
    pokemonMapItems (entity) {
      const rawSearch = String(this.mapSearch || '').trim().toLowerCase()
      const search = rawSearch === String(entity.name || '').toLowerCase() ? '' : rawSearch
      const grouped = Object.create(null)
      ;(this.spawnsByDexID[Number(entity.dexID)] || []).forEach(spawn => {
        const mapName = spawn.map || 'Unknown'
        if (!grouped[mapName]) grouped[mapName] = []
        grouped[mapName].push(spawn)
      })

      return Object.keys(grouped)
        .filter(mapName => !search || mapName.toLowerCase().indexOf(search) !== -1)
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
        .map(mapName => {
          const map = this.mapByName[mapName] || { name: mapName, land: 0, water: 0 }
          const representative = this.representativeTierSpawn(grouped[mapName])
          return {
            type: 'pokemonMap',
            map,
            dexID: entity.dexID,
            pokemonName: entity.name,
            memberOnly: grouped[mapName].length > 0 && grouped[mapName].every(spawn => spawn.memberOnly),
            repelInfo: this.showWarnings ? this.repelInfoForMapPokemon(mapName, entity.dexID) : null,
            tierLabel: this.searchTierLabel(representative, entity.dexID),
            tierGroup: representative ? this.rarityGroupKey(representative) : this.tierGroupFromTier(this.referenceTierForPokemon(entity.dexID))
          }
        })
    },
    regionMapItems (entity) {
      const rawSearch = String(this.mapSearch || '').trim().toLowerCase()
      const search = rawSearch === String(entity.name || '').toLowerCase() ? '' : rawSearch
      const regions = this.referenceData && this.referenceData.mapRegions ? this.referenceData.mapRegions : {}
      const names = regions[entity.name] || []
      return names
        .map(mapName => this.mapByLowerName[String(mapName).toLowerCase()])
        .filter(map => map)
        .filter(map => !search || map.name.toLowerCase().indexOf(search) !== -1)
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
        .map(map => ({ type: 'regionMap', region: entity.name, map }))
    },
    representativeTierSpawn (spawns) {
      if (!spawns || !spawns.length) return null
      return spawns.slice().sort((a, b) => this.raritySortValue(b) - this.raritySortValue(a))[0]
    },
    referenceTierForPokemon (dexID) {
      const tiers = this.referenceData && this.referenceData.pokemonRarityTiers ? this.referenceData.pokemonRarityTiers : {}
      return tiers[String(Number(dexID || 0))] || ''
    },
    referenceTierForSpawn (spawn) {
      return this.referenceTierForPokemon(spawn.dexID)
    },
    predefinedTierSortValueForSpawn (spawn) {
      const tier = this.referenceTierForSpawn(spawn)
      if (!tier) return 100
      return this.raritySortValueForTier(tier)
    },
    referenceTierSortValuesForPokemon (dexID) {
      const tier = this.referenceTierForPokemon(dexID)
      const tiers = Array.isArray(tier) ? tier : [tier]
      return tiers.map(entry => this.raritySortValueForTier(entry)).filter(value => value !== 99)
    },
    isSharedNatureWarningTier (spawn) {
      const value = this.raritySortValue(spawn)
      return value >= 5 && value <= 9.5
    },
    repelTierSortValue (spawn) {
      const spawnTier = this.raritySortValue(spawn)
      if (spawnTier !== 99) return spawnTier
      const referenceTier = this.predefinedTierSortValueForSpawn(spawn)
      return referenceTier === 100 ? 99 : referenceTier
    },
    normalizePokemonLookupName (name) {
      return String(name || '').trim().toLowerCase()
    },
    naturesForSpawn (spawn) {
      return this.preferredNaturesByDexID[String(Number(spawn.dexID || 0))] || []
    },
    spawnSupportsNature (spawn, nature) {
      return this.naturesForSpawn(spawn).indexOf(nature) !== -1
    },
    buildNatureConflictWarnings (spawns, area) {
      const sourceSpawns = (spawns || []).filter(spawn => this.isSharedNatureWarningTier(spawn))
      const grouped = Object.create(null)
      TIME_PERIODS.forEach(period => {
        const periodSpawns = sourceSpawns.filter(spawn => this.isTimeActive(spawn, period.key))
        const byNature = Object.create(null)
        periodSpawns.forEach(spawn => {
          const dexID = Number(spawn.dexID || 0)
          if (!dexID) return
          this.naturesForSpawn(spawn).forEach(nature => {
            if (!byNature[nature]) byNature[nature] = Object.create(null)
            if (!byNature[nature][dexID]) byNature[nature][dexID] = spawn
          })
        })
        Object.keys(byNature).forEach(nature => {
          const natureSpawns = Object.values(byNature[nature])
          if (natureSpawns.length < 3) return
          const dexIDs = natureSpawns.map(spawn => Number(spawn.dexID || 0)).sort((a, b) => a - b)
          const key = area + '-' + nature + '-' + dexIDs.join('-')
          if (!grouped[key]) {
            grouped[key] = {
              key,
              nature,
              dexIDs,
              spawns: natureSpawns,
              periods: []
            }
          }
          grouped[key].periods.push(period)
        })
      })

      return Object.values(grouped)
        .map(entry => {
          const dexIDSet = entry.dexIDs.reduce((result, dexID) => {
            result[dexID] = true
            return result
          }, Object.create(null))
          const spawnKeys = sourceSpawns
            .filter(spawn => dexIDSet[Number(spawn.dexID || 0)] && this.spawnSupportsNature(spawn, entry.nature))
            .map(spawn => this.spawnKey(spawn))

          return {
            key: 'nature-' + entry.key,
            nature: entry.nature,
            dexIDs: entry.dexIDs,
            spawnKeys,
            text: entry.nature + ' is shared by ' + this.sharedNatureTierRangeLabel(entry.spawns) + ' ' + area + ' spawns ' + entry.spawns.map(spawn => this.pokemonName(spawn)).sort().join(', ') + ' during ' + this.formatPeriodLabels(entry.periods) + '.'
          }
        })
        .sort((a, b) => a.text.localeCompare(b.text, undefined, { numeric: true }))
    },
    sharedNatureTierRangeLabel (spawns) {
      const tiers = (spawns || [])
        .map(spawn => this.raritySortValue(spawn))
        .filter(value => value >= 5 && value <= 9.5)
        .sort((a, b) => a - b)
        .filter((value, index, list) => index === 0 || value !== list[index - 1])
      if (!tiers.length) return 'T5-9.5'
      const first = tiers[0]
      const last = tiers[tiers.length - 1]
      if (first === last) return this.compactTierLabel(first)
      return this.compactTierLabel(first) + '-' + this.compactTierLabel(last).replace(/^T/, '')
    },
    compactTierLabel (tier) {
      return 'T' + String(tier).replace(/\.0$/, '')
    },
    uniqueDexIDs (spawns) {
      const seen = Object.create(null)
      return (spawns || [])
        .map(spawn => Number(spawn.dexID || 0))
        .filter(dexID => dexID)
        .filter(dexID => {
          if (seen[dexID]) return false
          seen[dexID] = true
          return true
        })
    },
    repelInfoForMap (mapName) {
      if (!this.showWarnings || !mapName) return Object.create(null)
      const cacheKey = [
        mapName,
        this.membershipActive ? 'member' : 'free',
        this.spawnsUpdatedAt || '',
        this.referenceDataUpdatedAt || ''
      ].join('|')
      if (!this.repelMapCache[cacheKey]) {
        this.repelMapCache[cacheKey] = this.buildRepelInfoBySpawnKey(this.spawnsByMap[mapName] || [])
      }
      return this.repelMapCache[cacheKey]
    },
    repelInfoForMapPokemon (mapName, dexID) {
      if (!this.showWarnings) return null
      const infoByKey = this.repelInfoForMap(mapName)
      const rows = (this.spawnsByMap[mapName] || []).filter(spawn => Number(spawn.dexID || 0) === Number(dexID || 0))
      for (let i = 0; i < rows.length; i++) {
        const info = infoByKey[this.spawnKey(rows[i])]
        if (info) return info
      }
      return null
    },
    repelInfoForPokemonRows (rows) {
      if (!this.showWarnings) return null
      for (let i = 0; i < (rows || []).length; i++) {
        const spawn = rows[i]
        const info = this.repelInfoForMap(spawn.map || 'Unknown')[this.spawnKey(spawn)]
        if (info) return info
      }
      return null
    },
    buildRepelInfoBySpawnKey (mapSpawns) {
      const bestBySpawn = Object.create(null)
      const analysisSpawns = (mapSpawns || []).filter(spawn => this.membershipActive || !spawn.memberOnly)
      TIME_PERIODS.forEach(period => {
        const periodSpawns = analysisSpawns.filter(spawn => this.isTimeActive(spawn, period.key))
        const activeDexCount = this.uniqueDexIDs(periodSpawns).length
        if (activeDexCount < 2) return
        const maxLevel = periodSpawns.reduce((max, spawn) => Math.max(max, Number(spawn.maxLVL || 0)), 0)
        for (let level = 1; level <= maxLevel; level++) {
          const eligible = periodSpawns.filter(spawn => Number(spawn.maxLVL || 0) >= level)
          const eligibleDexIDs = this.uniqueDexIDs(eligible)
          if (!eligible.length || eligibleDexIDs.length >= activeDexCount) continue

          let targets = []
          let reason = ''
          if (eligibleDexIDs.length === 1) {
            targets = eligible
            reason = 'solo'
          } else {
            const highTier = eligible.filter(spawn => this.repelTierSortValue(spawn) >= 7)
            if (highTier.length && highTier.length === eligible.length) {
              targets = highTier
              reason = 'high-tier'
            }
          }

          targets.forEach(spawn => {
            const dexID = Number(spawn.dexID || 0)
            if (!dexID) return
            const key = this.spawnKey(spawn)
            if (!bestBySpawn[key] || level < bestBySpawn[key].minLevel || (level === bestBySpawn[key].minLevel && reason === 'solo' && bestBySpawn[key].reason !== 'solo')) {
              bestBySpawn[key] = {
                dexID,
                name: this.pokemonName(spawn),
                minLevel: level,
                reason,
                periods: [period]
              }
            } else if (level === bestBySpawn[key].minLevel && reason === bestBySpawn[key].reason && !bestBySpawn[key].periods.some(existing => existing.key === period.key)) {
              bestBySpawn[key].periods.push(period)
            }
          })
        }
      })
      return bestBySpawn
    },
    repelInfoForSpawn (spawn) {
      return this.repelInfoBySpawnKey[this.spawnKey(spawn)] || null
    },
    repelTooltip (spawn) {
      const info = this.repelInfoForSpawn(spawn)
      if (!info) return ''
      return this.repelInfoTooltip(info)
    },
    repelInfoTooltip (info) {
      if (!info) return ''
      const reason = info.reason === 'solo' ? 'can be isolated' : 'leaves only T7+ Pokemon'
      return this.escapeHtml(info.name + ' ' + reason + ' with Repel from Lv. ' + info.minLevel + ' during ' + this.formatPeriodLabels(info.periods) + '.')
    },
    searchTierLabel (spawn, dexID) {
      const tier = spawn ? this.rarityTier(spawn) : this.referenceTierForPokemon(dexID)
      return this.tierDisplayLabel(tier)
    },
    tierDisplayLabel (tier) {
      if (Array.isArray(tier)) {
        if (!tier.length) return 'Custom'
        return tier.map(entry => this.tierDisplayLabel(entry)).join(', ')
      }
      const tierText = String(tier || '').trim()
      if (!tierText) return 'Custom'
      if (this.isTierTen(tierText)) return 'Tier 10'
      const match = /^T(.+)$/.exec(tierText)
      return match ? 'Tier ' + match[1] : tierText
    },
    addSpawnRaritySortValue (pokemon) {
      const values = this.referenceTierSortValuesForPokemon(pokemon.dexID)
      if (!values.length) return 99
      return Math.max.apply(null, values)
    },
    addSpawnPokemonTierLabel (dexID) {
      return this.tierDisplayLabel(this.referenceTiersForPokemon(dexID))
    },
    tierGroupFromTier (tier) {
      const sortValue = this.raritySortValueForTier(tier)
      if (sortValue >= 1 && sortValue <= 3) return 'common'
      if (sortValue >= 4 && sortValue <= 6) return 'uncommon'
      if (sortValue >= 7 && sortValue <= 9.5) return 'rare'
      if (sortValue === 10) return 'unobtainable'
      return this.tierClassKey(tier)
    },
    loadPreferences () {
      try {
        const raw = window.localStorage.getItem(SPAWN_EDITOR_PREFS_KEY)
        if (!raw) return
        const prefs = JSON.parse(raw)
        if (SORT_OPTIONS.some(option => option.value === prefs.sortKey)) {
          this.sortKey = prefs.sortKey
        }
        if (prefs.sortDirection === 'asc' || prefs.sortDirection === 'desc') {
          this.sortDirection = prefs.sortDirection
        }
        if (typeof prefs.membershipActive === 'boolean') {
          this.membershipActive = prefs.membershipActive
        }
        if (typeof prefs.showWarnings === 'boolean') {
          this.showWarnings = prefs.showWarnings
        }
      } catch (e) {
        console.warn('Failed to load spawn editor preferences.', e)
      }
    },
    savePreferences () {
      try {
        window.localStorage.setItem(SPAWN_EDITOR_PREFS_KEY, JSON.stringify({
          sortKey: this.sortKey,
          sortDirection: this.sortDirection,
          membershipActive: this.membershipActive,
          showWarnings: this.showWarnings
        }))
      } catch (e) {
        console.warn('Failed to save spawn editor preferences.', e)
      }
    },
    toggleSortDirection () {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
    },
    passesActiveFilters (spawn) {
      if (!this.membershipActive && spawn.memberOnly) return false
      if (this.activeTimeFilter !== 'all' && !this.isTimeActive(spawn, this.activeTimeFilter)) return false
      return true
    },
    compareNames (a, b) {
      return this.pokemonName(a).localeCompare(this.pokemonName(b), undefined, { numeric: true })
    },
    raritySortValue (spawn) {
      return this.raritySortValueForTier(this.rarityTier(spawn))
    },
    raritySortValueForTier (tier) {
      if (Array.isArray(tier)) {
        const values = tier.map(entry => this.raritySortValueForTier(entry))
        if (!values.length) return 99
        return Math.max.apply(null, values)
      }
      const tierText = String(tier || '').trim()
      const match = /^T(\d+(?:\.\d+)?)$/.exec(tierText)
      if (match) return Number(match[1])
      if (this.isTierTen(tierText)) return 10
      return 99
    },
    compareSpawns (a, b) {
      const direction = this.sortDirection === 'desc' ? -1 : 1
      let primaryCompare = 0
      if (this.sortKey === 'dexID') {
        primaryCompare = Number(a.dexID || 0) - Number(b.dexID || 0)
      } else if (this.sortKey === 'rarity') {
        primaryCompare = this.raritySortValue(a) - this.raritySortValue(b)
        if (primaryCompare === 0) {
          primaryCompare = Number(b.rarity || 0) - Number(a.rarity || 0)
        }
      } else {
        primaryCompare = this.compareNames(a, b)
      }
      if (primaryCompare !== 0) return direction * primaryCompare

      const nameCompare = this.compareNames(a, b)
      if (nameCompare !== 0) return direction * nameCompare
      const dexCompare = Number(a.dexID || 0) - Number(b.dexID || 0)
      if (dexCompare !== 0) return direction * dexCompare
      const levelCompare = Number(a.minLVL || 0) - Number(b.minLVL || 0)
      if (levelCompare !== 0) return direction * levelCompare
      const idCompare = Number(a.id || 0) - Number(b.id || 0)
      return direction * idCompare
    },
    spawnKey (spawn) {
      return String(spawn.area || 'land') + '-' + String(spawn.id || '') + '-' + String(spawn.dexID || '')
    },
    isWaterSpawn (spawn) {
      const area = String(spawn.area || '').toLowerCase()
      return area === 'surf' || area === 'water'
    },
    isFishable (spawn) {
      return Number(spawn.required_rod || 0) > 0
    },
    isFishingOnly (spawn) {
      return this.isWaterSpawn(spawn) &&
        Number(spawn.x1 || 0) === 0 &&
        Number(spawn.x2 || 0) === 0 &&
        Number(spawn.y1 || 0) === 0 &&
        Number(spawn.y2 || 0) === 0
    },
    hasSurfEncounter (spawn) {
      return this.isWaterSpawn(spawn) && !this.isFishingOnly(spawn)
    },
    rodLabel (spawn) {
      const rod = Number(spawn.required_rod || 0)
      if (rod === 1) return 'Old Rod+'
      if (rod === 2) return 'Good Rod+'
      if (rod === 3) return 'Super Rod'
      return 'Not fishable'
    },
    rodIconForSpawn (spawn) {
      const rod = Number(spawn.required_rod || 0)
      const match = FISHING_RODS.find(candidate => candidate.requiredRod === rod)
      return match ? match.icon : ''
    },
    rodIconForKey (key) {
      const match = FISHING_RODS.find(candidate => candidate.key === key)
      return match ? match.icon : ''
    },
    pokemonName (spawn) {
      if (spawn.pokemon) return spawn.pokemon
      const monster = MONSTER_BY_ID[Number(spawn.dexID)]
      return monster && monster.Name ? monster.Name : 'Unknown Pokemon'
    },
    pokemonSpriteUrl (id) {
      const numeric = Number(id)
      if (!numeric || Number.isNaN(numeric)) return ''
      return 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/' + numeric + '.png'
    },
    pokemonFallbackId (id) {
      const numeric = Number(id)
      if (!numeric || Number.isNaN(numeric)) return ''
      const current = MONSTER_BY_ID[numeric]
      if (!current || !current.Name) return ''
      const baseName = this.normalizePokemonName(current.Name)
      if (!baseName || baseName === current.Name) return ''
      const base = MONSTER_BY_NAME[baseName.toLowerCase()]
      if (!base || Number(base.ID) === numeric) return ''
      return base.ID
    },
    normalizePokemonName (name) {
      const raw = String(name || '')
      if (raw.indexOf('Rotom-') === 0 || raw.indexOf('Rotom ') === 0) {
        return 'Rotom'
      }
      return raw
        .replace(/\b(Alola|Alolan|Galarian|Hisuian|Paldean|Mega|Primal|Origin|Therian|Incarnate|Forme?|Mode|Sky|Midnight|Dusk|Dawn|Ultra|Gigantamax|Gmax|Fan|Wash|Heat|Frost|Mow)\b/gi, '')
        .replace(/\b(Attack|Defense|Speed|Complete|Ash|Black|White)\b/gi, '')
        .replace(/[-:]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    },
    handleSpriteError (spawn, event) {
      if (!event || !event.target) return
      const target = event.target
      const fallbackId = target.dataset ? target.dataset.fallbackId : ''
      if (fallbackId) {
        target.src = this.pokemonSpriteUrl(fallbackId)
        target.dataset.fallbackId = ''
        return
      }
      this.$set(this.spriteErrors, this.spawnKey(spawn), true)
    },
    handleSearchSpriteError (item, event) {
      if (!event || !event.target) return
      const target = event.target
      const fallbackId = target.dataset ? target.dataset.fallbackId : ''
      if (fallbackId) {
        target.src = this.pokemonSpriteUrl(fallbackId)
        target.dataset.fallbackId = ''
        return
      }
      this.$set(this.spriteErrors, 'search-' + item.dexID, true)
    },
    handleSearchMapSpriteError (item, event) {
      if (!event || !event.target) return
      const target = event.target
      const fallbackId = target.dataset ? target.dataset.fallbackId : ''
      if (fallbackId) {
        target.src = this.pokemonSpriteUrl(fallbackId)
        target.dataset.fallbackId = ''
        return
      }
      this.$set(this.spriteErrors, 'search-map-' + item.dexID + '-' + item.map.name, true)
    },
    handleAddSpawnSpriteError (pokemon, event) {
      if (!event || !event.target) return
      const target = event.target
      const fallbackId = target.dataset ? target.dataset.fallbackId : ''
      if (fallbackId) {
        target.src = this.pokemonSpriteUrl(fallbackId)
        target.dataset.fallbackId = ''
        return
      }
      this.$set(this.spriteErrors, 'add-' + pokemon.dexID, true)
    },
    handleAddSpawnSelectedSpriteError (pokemon, event) {
      if (!event || !event.target) return
      const target = event.target
      const fallbackId = target.dataset ? target.dataset.fallbackId : ''
      if (fallbackId) {
        target.src = this.pokemonSpriteUrl(fallbackId)
        target.dataset.fallbackId = ''
        return
      }
      this.$set(this.spriteErrors, 'add-selected-' + pokemon.dexID, true)
    },
    possibleMoves (spawn) {
      const dexID = Number(spawn.dexID || 0)
      const minLevel = Math.max(Number(spawn.minLVL || 0), 1)
      const maxLevel = Math.max(Number(spawn.maxLVL || minLevel), minLevel)
      const cacheKey = dexID + '-' + minLevel + '-' + maxLevel
      if (this.moveCache[cacheKey]) return this.moveCache[cacheKey]

      const learnset = this.movesByMonsterID[dexID] || []
      if (!learnset.length) {
        this.$set(this.moveCache, cacheKey, [])
        return []
      }

      const selected = Object.create(null)
      for (let level = minLevel; level <= maxLevel; level++) {
        const available = learnset.filter(move => move.level <= level)
        const lastFour = available.slice(Math.max(available.length - 4, 0))
        lastFour.forEach(move => {
          const key = String(Number(move.skillID || 0) || this.normalizeMoveName(move.name))
          if (!selected[key]) {
            selected[key] = Object.assign({}, move, { levels: [] })
          }
          selected[key].levels.push(level)
        })
      }

      const moves = Object.keys(selected)
        .map(key => {
          const move = selected[key]
          const ranges = this.compactLevelRanges(move.levels || [])
          return Object.assign({}, move, {
            ranges,
            rangeText: this.formatLevelRanges(ranges)
          })
        })
        .sort((a, b) => {
          const levelCompare = a.level - b.level
          if (levelCompare !== 0) return levelCompare
          return a.id - b.id
        })
      this.$set(this.moveCache, cacheKey, moves)
      return moves
    },
    moveTooltip (spawn) {
      const moves = this.possibleMoves(spawn)
      if (!moves.length) return 'No level-up moves found.'
      return moves.map(move => {
        const style = this.isSelfHarmingMove(move, spawn) ? ' style="color:#ff6b79;font-weight:700;"' : ''
        const range = move.rangeText ? ' <span style="color:#9fb0bf;">(' + this.escapeHtml(move.rangeText) + ')</span>' : ''
        return '<div' + style + '>Lv. ' + move.level + ': ' + this.escapeHtml(move.name) + range + '</div>'
      }).join('')
    },
    compactLevelRanges (levels) {
      const sorted = (levels || []).slice().sort((a, b) => a - b)
      const ranges = []
      let start = null
      let previous = null
      sorted.forEach(level => {
        if (start === null) {
          start = level
          previous = level
          return
        }
        if (level === previous + 1) {
          previous = level
          return
        }
        ranges.push({ start, end: previous })
        start = level
        previous = level
      })
      if (start !== null) {
        ranges.push({ start, end: previous })
      }
      return ranges
    },
    formatLevelRanges (ranges) {
      return (ranges || []).map(range => range.start === range.end ? String(range.start) : range.start + '-' + range.end).join(', ')
    },
    normalizeMoveName (move) {
      return String(move || '').trim().toLowerCase()
    },
    pokemonTypes (spawn) {
      return [spawn.type1, spawn.type2]
        .map(type => String(type || '').trim().toLowerCase())
        .filter(type => type)
    },
    hasPokemonType (spawn, type) {
      return this.pokemonTypes(spawn).indexOf(String(type || '').toLowerCase()) !== -1
    },
    isSelfHarmingMove (move, spawn) {
      const skillID = Number(move && move.skillID ? move.skillID : move)
      if (!skillID || !this.selfHarmingMoveSet[skillID]) return false
      const normalized = this.normalizeMoveName(move && move.name ? move.name : '')
      if (normalized === 'curse') {
        return this.hasPokemonType(spawn, 'ghost')
      }
      if (normalized === 'sandstorm') {
        return !this.pokemonTypes(spawn).some(type => SANDSTORM_SAFE_TYPES.indexOf(type) !== -1)
      }
      return true
    },
    highlightMapSearch (value) {
      const search = String(this.mapSearch || '').trim()
      if (!search) return this.escapeHtml(value)
      const text = String(value || '')
      const lowerText = text.toLowerCase()
      const lowerSearch = search.toLowerCase()
      const index = lowerText.indexOf(lowerSearch)
      if (index === -1) return this.escapeHtml(text)
      return this.escapeHtml(text.slice(0, index)) +
        '<span class="spawn-highlight">' + this.escapeHtml(text.slice(index, index + search.length)) + '</span>' +
        this.escapeHtml(text.slice(index + search.length))
    },
    escapeHtml (value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
    },
    hasItem (spawn) {
      return Number(spawn.itemID || 0) > 0
    },
    itemName (spawn) {
      if (!this.hasItem(spawn)) return '\u2014'
      if (spawn.item) return spawn.item
      return this.itemNameByID[String(Number(spawn.itemID || 0))] || ('#' + spawn.itemID)
    },
    itemDropLabel (spawn) {
      return this.formatPercent(Number(spawn.itemRarity || 0) * 100)
    },
    rawRarity (spawn) {
      const rarity = Number(spawn.rarity || 0)
      return rarity.toFixed(6).replace(/0+$/, '').replace(/\.$/, '')
    },
    rarityTier (spawn) {
      const rarity = Number(spawn.rarity || 0)
      const table = this.isWaterSpawn(spawn) ? WATER_RARITIES : LAND_RARITIES
      const match = table.find(entry => Math.abs(entry.value - rarity) < 0.0000001)
      return match ? match.tier : 'Custom'
    },
    rarityGroupKey (spawn) {
      const sortValue = this.raritySortValue(spawn)
      if (sortValue >= 1 && sortValue <= 3) return 'common'
      if (sortValue >= 4 && sortValue <= 6) return 'uncommon'
      if (sortValue >= 7 && sortValue <= 9.5) return 'rare'
      if (sortValue === 10) return 'unobtainable'
      return this.tierClassKey(this.rarityTier(spawn))
    },
    rarityGroupLabel (spawn) {
      const key = this.rarityGroupKey(spawn)
      if (key === 'common') return 'Common'
      if (key === 'uncommon') return 'Uncommon'
      if (key === 'rare') return 'Rare'
      if (key === 'unobtainable') return 'Unobtainable'
      return this.rarityTier(spawn)
    },
    isTierTen (tier) {
      const normalized = String(tier || '').trim().toLowerCase()
      return normalized === 't10' || normalized === 'tier 10' || normalized === 'tier x' || normalized === 'x' || normalized === 'unobtainable'
    },
    tierClassKey (tier) {
      return String(tier || 'custom').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'custom'
    },
    parseForms (spawn) {
      const seen = Object.create(null)
      return String(spawn.forms || '')
        .split(',')
        .map(value => Number(String(value).trim()))
        .filter(value => value > 0)
        .filter(value => {
          if (seen[value]) return false
          seen[value] = true
          return true
        })
        .sort((a, b) => a - b)
    },
    formEventName (speciesForms, formId) {
      const found = speciesForms.find(form => Number(form.id) === formId)
      return found && found.name ? found.name : 'Non-Event'
    },
    compactNumberRanges (ids) {
      const ranges = []
      let start = null
      let previous = null
      ids.forEach(id => {
        if (start === null) {
          start = id
          previous = id
          return
        }
        if (id === previous + 1) {
          previous = id
          return
        }
        ranges.push(start === previous ? String(start) : start + '-' + previous)
        start = id
        previous = id
      })
      if (start !== null) {
        ranges.push(start === previous ? String(start) : start + '-' + previous)
      }
      return ranges.join(',')
    },
    formLabels (spawn) {
      const formIds = this.parseForms(spawn)
      if (!formIds.length) return []
      const mappings = this.formMapping && this.formMapping.mappings ? this.formMapping.mappings : {}
      const speciesForms = mappings[Number(spawn.dexID)] || []
      const groups = []
      const indexes = Object.create(null)
      formIds.forEach(formId => {
        const name = this.formEventName(speciesForms, formId)
        if (indexes[name] === undefined) {
          indexes[name] = groups.length
          groups.push({ name: name, ids: [] })
        }
        groups[indexes[name]].ids.push(formId)
      })
      return groups.map(group => group.name + ' (' + this.compactNumberRanges(group.ids) + ')')
    },
    normalizeHour (value) {
      const hour = Number(value || 0)
      if (!Number.isFinite(hour)) return 0
      const normalized = hour % 24
      return normalized < 0 ? normalized + 24 : normalized
    },
    intervalsForRange (start, end) {
      const from = this.normalizeHour(start)
      const to = this.normalizeHour(end)
      if (from === 0 && to === 0) {
        return [[0, 24]]
      }
      if (from === to) {
        return []
      }
      if (from < to) {
        return [[from, to]]
      }
      return [[from, 24], [0, to]]
    },
    intervalsOverlap (a, b) {
      return a[0] < b[1] && b[0] < a[1]
    },
    activeTimeKeys (spawn) {
      if (spawn.noTimeSelected) return []
      const start = this.normalizeHour(spawn.start)
      const end = this.normalizeHour(spawn.end)
      if (start === 0 && end === 0) {
        return TIME_PERIODS.map(period => period.key)
      }
      const spawnIntervals = this.intervalsForRange(start, end)
      return TIME_PERIODS
        .filter(period => {
          const periodIntervals = this.intervalsForRange(period.start, period.end)
          return spawnIntervals.some(spawnInterval => periodIntervals.some(periodInterval => this.intervalsOverlap(spawnInterval, periodInterval)))
        })
        .map(period => period.key)
    },
    isTimeActive (spawn, key) {
      return this.activeTimeKeys(spawn).indexOf(key) !== -1
    },
    isLastActiveTime (spawn, key) {
      const keys = this.activeTimeKeys(spawn)
      return keys.length === 1 && keys[0] === key
    },
    setSpawnTimeActive (spawn, key, active) {
      const selected = this.activeTimeKeys(spawn).reduce((result, activeKey) => {
        result[activeKey] = true
        return result
      }, Object.create(null))
      if (active) {
        selected[key] = true
      } else {
        delete selected[key]
      }
      const keys = TIME_PERIODS.map(period => period.key).filter(periodKey => selected[periodKey])
      if (!keys.length) {
        this.$set(spawn, 'noTimeSelected', true)
        return
      }
      const range = this.timeRangeForKeys(keys)
      this.$set(spawn, 'noTimeSelected', false)
      this.$set(spawn, 'start', range.start)
      this.$set(spawn, 'end', range.end)
    },
    timeRangeForKeys (keys) {
      const selected = keys.reduce((result, key) => {
        result[key] = true
        return result
      }, Object.create(null))
      const morning = !!selected.morning
      const day = !!selected.day
      const night = !!selected.night
      if (morning && day && night) return { start: 0, end: 0 }
      if (morning && day) return { start: 4, end: 20 }
      if (day && night) return { start: 10, end: 4 }
      if (morning && night) return { start: 20, end: 10 }
      if (morning) return { start: 4, end: 10 }
      if (day) return { start: 10, end: 20 }
      return { start: 20, end: 4 }
    },
    probabilityPool (spawn, key) {
      const spawnIsWater = this.isWaterSpawn(spawn)
      return this.selectedSpawns.filter(row => {
        if (this.isWaterSpawn(row) !== spawnIsWater) return false
        if (!this.membershipActive && row.memberOnly) return false
        if (!this.isTimeActive(row, key)) return false
        if (Number(row.rarity || 0) <= 0) return false
        if (!spawnIsWater) return true
        return this.hasSurfEncounter(row)
      })
    },
    spawnProbability (spawn, key) {
      if (!this.isTimeActive(spawn, key)) return null
      if (!this.membershipActive && spawn.memberOnly) return 0
      if (this.isWaterSpawn(spawn) && !this.hasSurfEncounter(spawn)) return null
      const pool = this.probabilityPool(spawn, key)
      const total = pool.reduce((sum, row) => sum + Number(row.rarity || 0), 0)
      if (total <= 0) return null
      return Number(spawn.rarity || 0) / total * 100
    },
    fishingProbabilityPool (rod, key) {
      return this.selectedSpawns.filter(row => {
        if (!this.isWaterSpawn(row)) return false
        if (!this.isFishable(row)) return false
        if (Number(row.required_rod || 0) > rod.requiredRod) return false
        if (!this.membershipActive && row.memberOnly) return false
        if (!this.isTimeActive(row, key)) return false
        return Number(row.rarity || 0) > 0
      })
    },
    fishingProbability (spawn, rod, key) {
      if (!this.isWaterSpawn(spawn) || !this.isFishable(spawn)) return null
      if (Number(spawn.required_rod || 0) > rod.requiredRod) return null
      if (!this.isTimeActive(spawn, key)) return 0
      if (!this.membershipActive && spawn.memberOnly) return 0
      const pool = this.fishingProbabilityPool(rod, key)
      const total = pool.reduce((sum, row) => sum + Number(row.rarity || 0), 0)
      if (total <= 0) return 0
      return Number(spawn.rarity || 0) / total * 100
    },
    fishingChanceRows (spawn) {
      if (!this.isWaterSpawn(spawn) || !this.isFishable(spawn)) return []
      return FISHING_RODS
        .map(rod => {
          const chances = TIME_PERIODS.map(period => {
            const value = this.fishingProbability(spawn, rod, period.key)
            return {
              key: period.key,
              label: period.label.charAt(0),
              raw: value,
              value: value === null ? '0%' : this.formatPercent(value)
            }
          })
          const groupedChances = this.groupFishingChances(chances)
          return {
            key: rod.key,
            label: rod.label,
            icon: rod.icon,
            chances: groupedChances,
            hasChance: groupedChances.length > 0
          }
        })
        .filter(row => row.hasChance)
    },
    groupFishingChances (chances) {
      const groups = Object.create(null)
      ;(chances || []).forEach((chance, index) => {
        if (Number(chance.raw || 0) <= 0) return
        const key = chance.value
        if (!groups[key]) {
          groups[key] = {
            key: key,
            firstIndex: index,
            labels: [],
            value: chance.value
          }
        }
        groups[key].labels.push(chance.label)
      })
      return Object.values(groups)
        .sort((a, b) => a.firstIndex - b.firstIndex)
        .map(group => ({
          key: group.key + '-' + group.labels.join('-'),
          label: group.labels.join('/'),
          value: group.value
        }))
    },
    probabilityLabel (spawn, key) {
      const value = this.spawnProbability(spawn, key)
      if (value === null) {
        return '-'
      }
      return this.formatPercent(value)
    },
    formatPercent (value) {
      const numeric = Number(value)
      if (!Number.isFinite(numeric)) return '-'
      if (numeric === 0) return '0%'
      if (numeric < 0.01) return numeric.toFixed(4) + '%'
      if (numeric < 1) return numeric.toFixed(3) + '%'
      if (numeric < 10) return numeric.toFixed(2) + '%'
      return numeric.toFixed(1) + '%'
    }
  }
}
</script>

<style scoped>
.spawn-editor {
  color: #e6edf3;
}

.spawn-editor button:focus,
.spawn-editor .btn:focus,
.spawn-editor .btn.focus,
.spawn-editor /deep/ .btn:focus,
.spawn-editor /deep/ .nav-link:focus {
  outline: 0;
  box-shadow: none;
}

.spawn-layout {
  display: grid;
  grid-template-columns: minmax(240px, 300px) minmax(0, 1fr);
  gap: 18px;
  align-items: start;
}

.spawn-map-panel,
.spawn-toolbar-card,
.spawn-card {
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: #1f252b;
  border-radius: 8px;
}

.spawn-map-panel {
  position: sticky;
  top: 70px;
  max-height: calc(100vh - 92px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.spawn-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.spawn-panel-title {
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.78rem;
  letter-spacing: 0.04em;
}

.spawn-workflow-tabs {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  padding: 10px 12px 0;
}

.spawn-workflow-tabs .btn-group {
  flex-wrap: wrap;
}

.spawn-map-search {
  padding: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.spawn-map-search-mode {
  width: 96px;
  font-size: 0.8rem;
}

.spawn-map-list {
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px;
}

.spawn-search-context {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 2px 8px;
}

.spawn-map-item {
  width: 100%;
  min-width: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #c8d1dc;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 10px;
  text-align: left;
}

.spawn-map-item-stacked {
  align-items: flex-start;
}

.spawn-map-item:hover,
.spawn-map-item.active {
  background: #2a333c;
  color: #fff;
}

.spawn-map-name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.spawn-map-counts {
  display: inline-flex;
  gap: 4px;
  flex: 0 0 auto;
}

.spawn-sidebar-entity,
.spawn-pokemon-map-line {
  flex: 1 1 auto;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.spawn-sidebar-sprite-wrap {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: #15191e;
  color: #8f9baa;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 24px;
  overflow: hidden;
}

.spawn-sidebar-sprite {
  max-width: 22px;
  max-height: 22px;
  object-fit: contain;
}

.spawn-map-result {
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.spawn-map-header-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
}

.spawn-pokemon-map-line {
  color: #aeb9c5;
  font-size: 0.78rem;
}

.spawn-region-map-line {
  color: #8f9baa;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
}

.spawn-highlight {
  background: rgba(32, 168, 216, 0.28);
  border-radius: 3px;
  color: #fff;
  padding: 0 2px;
}

.spawn-toolbar-card {
  margin-bottom: 18px;
}

.spawn-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 14px;
}

.spawn-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
}

.spawn-controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px 10px;
}

.spawn-check {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #d7dee6;
  font-size: 0.85rem;
}

.spawn-control-group {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.spawn-control-label {
  color: #8f9baa;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.spawn-sort-select {
  flex: 0 0 108px;
  width: 108px;
}

.spawn-sort-direction {
  min-width: 78px;
  white-space: nowrap;
}

.spawn-membership-toggle {
  white-space: nowrap;
}

.spawn-warning-toggle {
  white-space: nowrap;
}

.spawn-nature-summary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 14px;
}

.spawn-nature-pill {
  border: 1px solid rgba(32, 168, 216, 0.28);
  border-radius: 6px;
  background: #17212a;
  color: #d9edf7;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 8px;
  font-size: 0.78rem;
}

.spawn-nature-pill:hover,
.spawn-nature-pill.active {
  border-color: rgba(32, 168, 216, 0.72);
  background: #1b3340;
  color: #fff;
}

.spawn-nature-pill strong {
  color: #79d6f2;
}

.spawn-nature-pill small {
  color: #9fb0bf;
}

.spawn-section {
  margin-bottom: 24px;
}

.spawn-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  padding: 0 2px;
}

.spawn-section-header > div {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.spawn-section-actions {
  flex-wrap: wrap;
  justify-content: flex-end;
}

.spawn-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 10px;
}

.spawn-card {
  overflow: hidden;
}

.spawn-card-warning {
  background: #2b2024;
  border-color: rgba(248, 108, 107, 0.32);
}

.spawn-card-warning .spawn-meta-item,
.spawn-card-warning .spawn-time-cell {
  background: #24191d;
}

.spawn-card-nature-highlight {
  border-left-color: #79d6f2;
  box-shadow: 0 0 0 2px rgba(121, 214, 242, 0.72);
}

.spawn-card-land {
  border-left: 3px solid #4dbd74;
}

.spawn-card-water {
  border-left: 3px solid #20a8d8;
}

.spawn-card-land.spawn-card-nature-highlight,
.spawn-card-water.spawn-card-nature-highlight {
  border-left-color: #79d6f2;
}

.spawn-card-main {
  display: grid;
  grid-template-columns: 74px minmax(0, 1fr);
  gap: 10px;
  padding: 10px;
}

.spawn-sprite-column {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.spawn-sprite-wrap {
  width: 74px;
  height: 74px;
  border-radius: 8px;
  background: #15191e;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.spawn-sprite {
  max-width: 68px;
  max-height: 68px;
  object-fit: contain;
}

.spawn-sprite-fallback {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.16);
  color: #8f9baa;
  display: flex;
  align-items: center;
  justify-content: center;
}

.spawn-move-trigger {
  width: 70px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid rgba(248, 108, 107, 0.45);
  background: #24191d;
  color: #ffb0b0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: help;
  overflow: hidden;
}

.spawn-move-trigger:hover {
  border-color: rgba(248, 108, 107, 0.85);
  color: #fff;
}

.spawn-type-icon {
  width: 64px;
  height: auto;
  max-width: 64px;
  max-height: 22px;
  object-fit: contain;
}

.spawn-move-text {
  font-size: 0.62rem;
  font-weight: 800;
  text-transform: uppercase;
}

.spawn-card-body {
  min-width: 0;
}

.spawn-card-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.spawn-card-heading-actions {
  display: inline-flex;
  align-items: flex-start;
  gap: 8px;
  flex: 0 0 auto;
}

.spawn-pokemon {
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
  overflow-wrap: anywhere;
}

.spawn-membership-name {
  color: #ff6fae !important;
}

.spawn-subtitle {
  color: #8f9baa;
  font-size: 0.78rem;
}

.spawn-pvp-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.spawn-pvp-badge {
  border-radius: 999px;
  background: rgba(248, 108, 107, 0.16);
  border: 1px solid rgba(248, 108, 107, 0.45);
  color: #ff9a9a;
  font-size: 0.68rem;
  font-weight: 800;
  padding: 2px 7px;
}

.spawn-tier {
  flex: 0 0 auto;
  min-width: 42px;
  text-align: center;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  color: #fff;
  background: #59616b;
}

.tier-group-common {
  background: #46c46f;
  color: #082813;
}

.tier-group-uncommon {
  background: #ef8f2f;
  color: #2f1a00;
}

.tier-group-rare {
  background: #c93f4f;
  color: #fff;
}

.spawn-tier-compact {
  min-width: 0;
  padding: 2px 6px;
  font-size: 0.68rem;
}

.tier-group-custom {
  background: #59616b;
}

.tier-group-unobtainable {
  background: #6c757d;
}

.spawn-meta-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
  margin-bottom: 8px;
}

.spawn-meta-item {
  min-width: 0;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  background: #171c21;
  padding: 5px 6px;
}

.spawn-meta-wide {
  grid-column: 1 / -1;
}

.spawn-meta-item > span,
.spawn-time-label {
  display: block;
  color: #8f9baa;
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 2px;
}

.spawn-meta-item strong {
  display: block;
  color: #f7fafc;
  overflow-wrap: anywhere;
}

.spawn-level-line {
  display: flex !important;
  align-items: center;
  justify-content: flex-start;
  gap: 7px;
  text-align: left;
}

.spawn-repel-icon {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: rgba(248, 108, 107, 0.16);
  border: 1px solid rgba(248, 108, 107, 0.42);
  color: #ff9a9a;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 22px;
  line-height: 1;
  margin-bottom: 0;
  cursor: help;
}

.spawn-repel-icon img,
.spawn-repel-inline-icon img {
  display: block;
  width: 18px;
  height: 18px;
  object-fit: contain;
  object-position: center;
}

.spawn-warning-title-icon,
.spawn-item-icon {
  max-width: 18px;
  max-height: 18px;
  object-fit: contain;
}

.spawn-repel-inline-icon {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: rgba(248, 108, 107, 0.16);
  border: 1px solid rgba(248, 108, 107, 0.42);
  color: #ff9a9a;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 22px;
  cursor: help;
}

.spawn-meta-item small {
  color: #9fb0bf;
}

.spawn-inline-level-edit {
  display: grid;
  grid-template-columns: minmax(76px, 1fr) auto minmax(76px, 1fr);
  align-items: center;
  gap: 5px;
}

.spawn-inline-level-edit input {
  min-width: 76px;
}

.spawn-flags {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.spawn-fishing-icon {
  width: 20px;
  height: 20px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.spawn-fishing-only {
  color: #ffc107;
  font-size: 0.78rem;
  font-weight: 700;
}

.spawn-forms {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.spawn-form-badge {
  border-radius: 999px;
  background: rgba(255, 193, 7, 0.14);
  border: 1px solid rgba(255, 193, 7, 0.32);
  color: #ffe08a;
  font-size: 0.76rem;
  padding: 3px 8px;
}

.spawn-forms small {
  color: #b4c0cb;
}

.spawn-time-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
}

.spawn-time-cell {
  min-height: 62px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: #15191e;
  padding: 5px;
  text-align: center;
}

.spawn-time-cell.active {
  border-color: rgba(32, 168, 216, 0.45);
  background: #16242c;
}

.spawn-time-cell input {
  margin: 3px 0 4px;
}

.spawn-time-percent {
  color: #f7fafc;
  font-weight: 700;
  font-size: 0.82rem;
  overflow-wrap: anywhere;
}

.spawn-fishing-chances {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 6px;
}

.spawn-fishing-chance-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
  border-radius: 6px;
  background: #121820;
  border: 1px solid rgba(32, 168, 216, 0.22);
  color: #d9edf7;
  font-size: 0.72rem;
  padding: 4px 6px;
}

.spawn-fishing-chance-row > span:first-child {
  color: #79d6f2;
  font-weight: 700;
  min-width: 78px;
}

.spawn-fishing-rod-label {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.spawn-self-harm-warning {
  border-radius: 8px;
}

.spawn-warning-title {
  display: flex;
  align-items: center;
  gap: 5px;
  font-weight: 700;
  margin-bottom: 4px;
}

.spawn-warning-line {
  font-size: 0.86rem;
}

.spawn-warning-more {
  border: 0;
  background: transparent;
  color: #7bd7f4;
  font-size: 0.86rem;
  font-weight: 700;
  padding: 2px 0 0;
  cursor: pointer;
}

.spawn-warning-more:hover {
  color: #bcefff;
  text-decoration: underline;
}

.spawn-workflow-panel,
.spawn-empty-level-range {
  background: #1f2933;
  border: 1px solid #344150;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 16px;
}

.spawn-workflow-meta,
.spawn-workflow-actions,
.spawn-review-list,
.spawn-add-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.spawn-workflow-meta {
  justify-content: space-between;
}

.spawn-review-list {
  margin-top: 10px;
}

.spawn-delete-all-row {
  display: flex;
  justify-content: flex-end;
  margin: 0 2px 10px;
}

.spawn-review-pill {
  border: 1px solid #516172;
  border-radius: 4px;
  padding: 3px 7px;
  font-size: 0.78rem;
}

.spawn-review-approved {
  color: #8fe0a4;
  border-color: #3a7d4d;
}

.spawn-review-rejected {
  color: #ff9aa8;
  border-color: #9f4550;
}

.spawn-compare-table {
  margin-top: 12px;
  border: 1px solid #344150;
  border-radius: 6px;
  overflow: hidden;
}

.spawn-compare-row {
  display: grid;
  grid-template-columns: 82px 70px 150px minmax(0, 1fr) minmax(0, 1fr);
  gap: 8px;
  padding: 7px 9px;
  font-size: 0.78rem;
  border-top: 1px solid #344150;
}

.spawn-compare-row:first-child {
  border-top: 0;
}

.spawn-compare-head {
  background: #263443;
  color: #9fb0bf;
  font-weight: 700;
}

.spawn-compare-empty {
  padding: 10px;
  color: #9fb0bf;
  font-size: 0.85rem;
}

.spawn-empty-level-range {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 420px;
}

.spawn-empty-level-range input {
  width: 90px;
}

.spawn-edit-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(96px, 1fr));
  gap: 8px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #344150;
}

.spawn-edit-grid label {
  margin: 0;
}

.spawn-edit-grid label > span {
  display: block;
  color: #9fb0bf;
  font-size: 0.72rem;
  margin-bottom: 3px;
}

.spawn-edit-check {
  display: flex;
  align-items: center;
  gap: 6px;
  align-self: end;
  min-height: 31px;
}

.spawn-edit-check span {
  margin-bottom: 0 !important;
}

.spawn-add-layout {
  display: grid;
  grid-template-columns: 260px minmax(360px, 1fr) 300px;
  gap: 12px;
  min-height: 576px;
}

.spawn-add-panel {
  min-width: 0;
  border: 1px solid #344150;
  border-radius: 6px;
  background: #17212b;
  padding: 10px;
}

.spawn-add-filter-panel,
.spawn-add-selected-panel {
  max-height: calc(90vh - 189px);
  overflow: auto;
}

.spawn-add-available-panel {
  display: flex;
  flex-direction: column;
  min-height: 576px;
}

.spawn-add-selected-panel {
  border-style: dashed;
}

.spawn-add-selected-panel.dragging {
  border-color: #39b2d6;
  background: #1b2b36;
}

.spawn-add-control-block {
  border-bottom: 1px solid #2d3a47;
  padding-bottom: 10px;
  margin-bottom: 10px;
}

.spawn-add-control-title {
  color: #9fb0bf;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 6px;
}

.spawn-add-area-toggle {
  width: 100%;
}

.spawn-add-area-toggle .btn {
  flex: 1 1 0;
}

.spawn-add-level-edit {
  display: grid;
  grid-template-columns: minmax(72px, 1fr) auto minmax(72px, 1fr);
  align-items: center;
  gap: 6px;
}

.spawn-add-level-warning {
  color: #ffb0b0;
  font-size: 0.78rem;
  margin-top: 6px;
}

.spawn-add-filter-actions {
  margin-bottom: 10px;
}

.spawn-add-filter-group {
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  background: #121a22;
  padding: 8px;
  margin-bottom: 8px;
}

.spawn-add-filter-group.disabled {
  opacity: 0.56;
}

.spawn-add-filter-heading {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 7px;
  margin: 0;
  color: #d9e2ec;
  font-weight: 700;
  font-size: 0.82rem;
}

.spawn-add-filter-heading small {
  color: #9fb0bf;
  font-weight: 600;
}

.spawn-add-filter-options {
  display: grid;
  gap: 5px;
  margin-top: 8px;
  max-height: 138px;
  overflow: auto;
  padding-right: 2px;
}

.spawn-add-filter-option {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 6px;
  margin: 0;
  color: #cbd5df;
  font-size: 0.78rem;
}

.spawn-add-filter-option span {
  overflow-wrap: anywhere;
}

.spawn-add-toolbar {
  margin-bottom: 12px;
}

.spawn-add-toolbar .form-control {
  min-width: 160px;
  flex: 1 1 220px;
}

.spawn-add-sort {
  max-width: 120px;
}

.spawn-add-result-summary {
  color: #9fb0bf;
  font-size: 0.82rem;
  margin: 8px 0;
}

.spawn-add-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, 82px);
  justify-content: start;
  align-content: start;
  grid-auto-rows: minmax(94px, auto);
  gap: 6px;
  flex: 1 1 auto;
  max-height: calc(90vh - 261px);
  min-height: 0;
  overflow: auto;
  padding-right: 4px;
}

.spawn-add-pokemon {
  background: #17212b;
  color: #d9e2ec;
  border: 1px solid #344150;
  border-radius: 6px;
  width: 82px;
  min-height: 94px;
  padding: 6px 5px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  cursor: grab;
}

.spawn-add-pokemon:hover {
  border-color: #39b2d6;
  background: #203241;
}

.spawn-add-pokemon:focus,
.spawn-add-pokemon:active,
.spawn-add-modal .form-control:focus,
.spawn-add-modal .custom-select:focus {
  outline: 0;
  box-shadow: none;
}

.spawn-add-sprite {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.spawn-add-sprite img {
  max-width: 42px;
  max-height: 42px;
}

.spawn-add-name {
  font-size: 0.72rem;
  line-height: 1.15;
  min-height: 24px;
  display: flex;
  align-items: center;
  text-align: center;
}

.spawn-add-selected-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #d9e2ec;
  font-weight: 700;
  margin-bottom: 10px;
}

.spawn-add-selected-title strong {
  color: #79d6f2;
}

.spawn-add-drop-empty {
  min-height: 150px;
  border: 1px dashed rgba(255, 255, 255, 0.16);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8f9baa;
  font-size: 0.84rem;
}

.spawn-add-selected-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.spawn-add-selected-row {
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  border: 1px solid #344150;
  border-radius: 6px;
  background: #121a22;
  padding: 6px;
}

.spawn-add-selected-sprite {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.spawn-add-selected-sprite img {
  max-width: 40px;
  max-height: 40px;
}

.spawn-add-selected-name {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.spawn-add-selected-name strong,
.spawn-add-selected-name small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.spawn-add-selected-name strong {
  color: #f7fafc;
  font-size: 0.82rem;
}

.spawn-add-selected-name small {
  color: #9fb0bf;
  font-size: 0.72rem;
}

.spawn-add-remove {
  width: 28px;
  height: 28px;
  border: 1px solid rgba(248, 108, 107, 0.45);
  border-radius: 6px;
  background: #24191d;
  color: #ff9a9a;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.spawn-add-remove:hover {
  border-color: rgba(248, 108, 107, 0.85);
  color: #fff;
}

.spawn-loading,
.spawn-empty,
.spawn-empty-section {
  min-height: 160px;
  border: 1px dashed rgba(255, 255, 255, 0.14);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #8f9baa;
}

.spawn-empty i {
  font-size: 2rem;
  margin-bottom: 8px;
}

.spawn-empty-section {
  min-height: 80px;
}

@media (max-width: 991.98px) {
  .spawn-layout {
    grid-template-columns: 1fr;
  }

  .spawn-map-panel {
    position: static;
    max-height: 320px;
  }

  .spawn-toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .spawn-controls {
    justify-content: flex-start;
  }

  .spawn-add-layout {
    grid-template-columns: 1fr;
    min-height: 0;
  }

  .spawn-add-available-panel {
    min-height: 420px;
  }

  .spawn-add-filter-panel,
  .spawn-add-selected-panel {
    max-height: none;
  }
}

@media (max-width: 575.98px) {
  .spawn-grid {
    grid-template-columns: 1fr;
  }

  .spawn-card-main {
    grid-template-columns: 1fr;
  }

  .spawn-sprite-column {
    align-items: stretch;
  }

  .spawn-sprite-wrap {
    width: 100%;
  }

  .spawn-move-trigger {
    align-self: center;
  }

  .spawn-meta-grid,
  .spawn-time-grid {
    grid-template-columns: 1fr;
  }
}
</style>

<style>
.spawn-add-modal .modal-dialog {
  max-width: 1740px;
  width: calc(100vw - 30px);
}

.spawn-add-modal .modal-body {
  overflow: hidden;
}

.spawn-add-modal .form-control:focus,
.spawn-add-modal .custom-select:focus,
.spawn-add-modal button:focus {
  outline: 0;
  box-shadow: none;
}

@media (max-width: 991.98px) {
  .spawn-add-modal .modal-body {
    overflow: auto;
  }
}
</style>
